import {
  createElementFromHTML,
  stringToBoolean,
  isMobile,
  getLazyLoadComponentObserver,
  handleize,
  getPromotionDiscount,
  renderPriceMarkup,
} from "@utilities/theme";
import { EVENTS, subscribe } from "@utilities/events";
import { fetchVariantData as resolveVariant, prefetchVariantData as prefetchVariant, isVariantFullyAvailable, buildProductUrl } from "@utilities/product";
import "@components/embla-slider";

const cardMediaDomParser = new DOMParser();

if (!customElements.get("product-card")) {
  /**
   * ProductCard custom element that represents a product card on the site
   * Handles rendering, interactions, and data management for product cards
   * @class
   * @extends {HTMLElement}
   */
  class ProductCard extends HTMLElement {
    /**
     * Creates an instance of ProductCard
     * Sets up initial properties and state
     * @constructor
     */
    constructor() {
      super();
      // disable gallery flag
      this.disableGallery = this.dataset.disableGallery
        ? stringToBoolean(this.dataset.disableGallery)
        : false;
      // show sold out state flag
      this.showSoldOutState = this.dataset.showSoldOutState
        ? stringToBoolean(this.dataset.showSoldOutState)
        : false;
      // default variant id
      this.defaultVariantId = this.dataset.defaultVariantId
        ? parseInt(this.dataset.defaultVariantId, 10)
        : null;
      // elements
      this.elements = {};
      this.templates = {};
      // atc on variant resolve flag
      this.atcOnVariantResolve = true;
      // promotion
      this.promotion = {};
      // last undiscounted price markup, used to clear an applied discount
      this.lastPriceHtml = null;
      // whether a discount is currently painted over the server price markup
      this.promotionApplied = false;
      // variant
      this.variant = {};
      // slider
      this.slider = null;
      this.sliderComponent = null;
      // selected swatch data
      this.selectedSwatchData = null;
      // selected swatch element
      this.selectedSwatchElement = null;
      this.prefetchedSwatchMediaUrls = new Set();
      // Render-then-fetch: the grid injects a near-complete visible card body and
      // a data-card-payload. We render the preview from that payload and defer
      // the per-card product-json fetch until idle or first interaction.
      this.cardPayload = null;
      this.deferredHydrationStarted = false;
      this.pendingSwatchSelection = null;
      // When the painted media is preserved on hydrate, the gallery slider is
      // built lazily on first media interaction. True until that build runs.
      this.deferredMediaSliderPending = false;
      this.boundStartDeferredHydration = null;
      this.deferredHydrationIdleId = null;
      this.unsubscribers = [];
      this.boundHandlePromotionChange = this.handlePromotionChange.bind(this);
      this.boundHandleViewportModeChange = this.handleViewportModeChange.bind(this);
      this.boundHandleQuickAddVariantChange = this.handleQuickAddVariantChange.bind(this);
      this.viewportModeRaf = null;
      this.currentMediaMode = null;
      this.customDotsObserver = null;
      this.mediaPointerState = {
        isPointerDown: false,
        moved: false,
        startX: 0,
        startY: 0,
        suppressClickUntil: 0,
      };
    }
    /**
     * Determines if lazy loading is enabled for this product card
     * @returns {boolean} Whether lazy loading is enabled
     */
    isLazyLoad() {
      // return true if the component has the lazy-load class
      return this.classList.contains("lazy-load");
    }

    isServerRendered() {
      return this.dataset.render === "server";
    }

    /**
     * Determines if debugging is enabled for this product card
     * Returns true if the card has debug class or global debug is enabled
     * @returns {boolean} Whether debugging is enabled
     */
    allowDebug() {
      // return true if the component has the debug class
      return this.classList.contains("debug");
    }

    isPriorityFastPathEnabled() {
      // Shell markup is vendor-agnostic; both Nosto and Shopify grids can set
      // priorityMode (PLP LCP plan Phase 4).
      return (
        (this.dataset.vendor === "nosto" || this.dataset.vendor === "shopify") &&
        this.dataset.priorityMode === "true"
      );
    }

    /**
     * Priority hydration deferral (PLP LCP plan Phase 5).
     *
     * Priority cards already painted a shell image; their per-card
     * `?view=product-json` fetch (plus spotlight/sale variant fetch chains)
     * otherwise contends with LCP image bandwidth. Defer the whole init data
     * path until the shell image has loaded/painted (bounded by a timeout) so
     * the LCP request wins. Default ON for priority shells.
     *
     * Kill switches: data-defer-hydration="false", ?plpDeferHydration=0,
     * localStorage plpDeferHydration=0.
     * @returns {boolean}
     */
    shouldDeferPriorityHydration() {
      if (!this.isPriorityFastPathEnabled()) return false;
      // Only defer when a shell/preview is actually painted; otherwise the card
      // shows a bare skeleton and should hydrate as fast as possible.
      if (
        !this.querySelector(
          "[data-priority-shell='true'], .product-card-skeleton--priority",
        )
      ) {
        return false;
      }
      if (this.dataset.deferHydration === "false") return false;

      const disabledValues = ["0", "false", "off"];
      try {
        const params = new URLSearchParams(window.location.search);
        const queryFlag = String(params.get("plpDeferHydration") || "").toLowerCase();
        if (disabledValues.includes(queryFlag)) return false;
      } catch (_error) {
        // no-op
      }

      try {
        const storageFlag = String(
          window.localStorage?.getItem("plpDeferHydration") || "",
        ).toLowerCase();
        if (disabledValues.includes(storageFlag)) return false;
      } catch (_error) {
        // no-op
      }

      return true;
    }

    /**
     * Resolve after the priority shell image has loaded and painted (double rAF
     * after the load event), bounded by a timeout. Resolves early on
     * pointerenter/focusin so hover/focus features (swatch gallery, quick add)
     * are never blocked behind a slow image — the shell anchor itself stays
     * clickable the whole time.
     * @param {Object} options
     * @param {number} [options.timeout=1500] - Max wait in ms
     * @returns {Promise<void>}
     */
    waitForPriorityShellPaint({ timeout = 1500 } = {}) {
      return new Promise((resolve) => {
        let settled = false;
        let timeoutId = null;

        const shellImage = this.querySelector(
          "[data-priority-shell='true'] img, .product-card-skeleton--priority img",
        );

        const finish = () => {
          if (settled) return;
          settled = true;
          this.removeEventListener("pointerenter", finish);
          this.removeEventListener("focusin", finish);
          if (shellImage) {
            shellImage.removeEventListener("load", afterPaint);
            shellImage.removeEventListener("error", afterPaint);
          }
          if (timeoutId) clearTimeout(timeoutId);
          resolve();
        };

        const afterPaint = () => {
          requestAnimationFrame(() => requestAnimationFrame(finish));
        };

        // Interactivity guard: resume immediately on intent to interact.
        this.addEventListener("pointerenter", finish);
        this.addEventListener("focusin", finish);

        timeoutId = setTimeout(finish, timeout);

        if (!shellImage || (shellImage.complete && shellImage.naturalWidth > 0)) {
          afterPaint();
        } else {
          shellImage.addEventListener("load", afterPaint, { once: true });
          shellImage.addEventListener("error", afterPaint, { once: true });
        }
      });
    }

    /**
     * Parse the vendor-agnostic CardPayload injected by the grid. Returns null
     * when absent or incomplete (the card then falls back to fetch-first init).
     * @returns {Object|null}
     */
    parseCardPayload() {
      if (!this.dataset.cardPayload) return null;
      try {
        const payload = JSON.parse(this.dataset.cardPayload);
        return payload && payload.complete ? payload : null;
      } catch (_error) {
        return null;
      }
    }

    /**
     * Render-then-fetch preview: the grid already injected a complete-looking
     * visible body, so there is nothing to paint here. We wire up the minimal
     * behavior the preview needs before the full product-json arrives:
     *  - a synthetic minimal variant (id/url/price/color) so click handlers and
     *    setDefaultVariant have something to work with;
     *  - listeners that swap the main image when a swatch is clicked (using the
     *    per-color selected_image carried in the payload) and remember the
     *    pending selection so the full hydrate applies it;
     *  - the deferred-hydration triggers.
     * @param {Object} payload - CardPayload
     */
    renderFromCardPayload(payload) {
      this.classList.add("product-card--preview");

      // Synthetic minimal variant from the payload. setDefaultVariant() will
      // overwrite this once the real product-json resolves.
      const defaultSwatch =
        Array.isArray(payload.swatches) && payload.swatches.length
          ? payload.swatches.find((swatch) => swatch.available !== false) ||
            payload.swatches[0]
          : null;
      this.variant = {
        id: payload.defaultVariantId,
        url: payload.url,
        price_html: payload.price?.price_html || null,
        color: defaultSwatch?.color || "",
        options: [],
      };
      this.selectedSwatchData = defaultSwatch
        ? { ...defaultSwatch }
        : null;

      this.wirePreviewSwatchListeners();
    }

    /**
     * Wire click/keyboard listeners on the injected preview swatches so a click
     * before hydration (a) swaps the main image to the per-color image and (b)
     * records the pending selection to be applied once product-json resolves.
     * Also starts the deferred hydration (a swatch click is interaction intent).
     */
    wirePreviewSwatchListeners() {
      const swatchInputs = this.querySelectorAll(
        ".product-card-swatches__container .product__option-swatch",
      );
      if (!swatchInputs.length) return;

      this.boundPreviewSwatchHandler = (event) => {
        const input = event.currentTarget;
        const selectedImage = input.dataset.selectedImage || "";
        this.pendingSwatchSelection = {
          optionValueId: input.dataset.optionValueId || "",
          color: input.dataset.color || "",
        };
        if (selectedImage) {
          const previewImage = this.querySelector(
            "[data-priority-shell='true'] .product-card__media img, .product-card__media img",
          );
          if (previewImage) previewImage.src = selectedImage;
        }
        this.startDeferredHydration();
      };

      for (const input of swatchInputs) {
        input.addEventListener("change", this.boundPreviewSwatchHandler);
      }
    }

    /**
     * Schedule the deferred product-json hydration. Fires on the first of:
     * idle (bounded), pointerenter, or focusin. A swatch click / quick-add
     * intent also calls startDeferredHydration() directly. Idempotent.
     */
    scheduleDeferredHydration() {
      if (this.deferredHydrationStarted) return;

      this.boundStartDeferredHydration = () => this.startDeferredHydration();

      this.addEventListener("pointerenter", this.boundStartDeferredHydration);
      this.addEventListener("focusin", this.boundStartDeferredHydration);

      const idle =
        window.requestIdleCallback ||
        ((cb) => window.setTimeout(() => cb({ didTimeout: true }), 1200));
      this.deferredHydrationIdleId = idle(
        () => this.startDeferredHydration(),
        { timeout: 2500 },
      );
    }

    /**
     * Begin the full hydration (fetch product-json → render). Idempotent; clears
     * the deferred-hydration triggers first. No-op if disconnected.
     */
    startDeferredHydration() {
      if (this.deferredHydrationStarted) return;
      this.deferredHydrationStarted = true;
      this.teardownDeferredHydrationTriggers();
      if (!this.isConnected) return;
      // Run the full data path; errors are handled inside hydrateFromFetch().
      this.hydrateFromFetch();
    }

    teardownDeferredHydrationTriggers() {
      if (this.boundStartDeferredHydration) {
        this.removeEventListener(
          "pointerenter",
          this.boundStartDeferredHydration,
        );
        this.removeEventListener("focusin", this.boundStartDeferredHydration);
        this.boundStartDeferredHydration = null;
      }
      if (this.deferredHydrationIdleId != null) {
        if (window.cancelIdleCallback) {
          window.cancelIdleCallback(this.deferredHydrationIdleId);
        } else {
          window.clearTimeout(this.deferredHydrationIdleId);
        }
        this.deferredHydrationIdleId = null;
      }
    }

    clearSlot(container, selector) {
      const slot = container?.querySelector(selector);
      if (slot) slot.replaceChildren();
      return slot;
    }

    /**
     * Lifecycle callback when the element is added to the DOM
     * Either initializes the component or sets up lazy loading
     */
    async connectedCallback() {
      // SSR skeleton: lazy-load class but no fetch URL and no payload.
      // Stay inert — the grid will call arm() when products arrive.
      if (this.isLazyLoad() && !this.dataset.fetchUrl && !this.dataset.cardPayload) {
        return;
      }

      if (this.isLazyLoad()) {
        const hasPriorityShell = !!this.querySelector(
          "[data-priority-shell='true']",
        );
        const hasPayload = !!this.dataset.cardPayload;

        if (hasPriorityShell || hasPayload) {
          getLazyLoadComponentObserver().observe(this);
        } else {
          // Lazy card with no payload — already viewport-gated by the grid's
          // deferred slot observer. Init directly (fetch-first hydration).
          await this.init();
        }
      } else {
        await this.init();
      }
    }

    /**
     * Arm an inert SSR skeleton with product data and trigger hydration.
     * Called by product-grid when products arrive — sets data attributes on
     * the existing <product-card> element and kicks off init().
     * @param {Object} config
     * @param {Object} config.product - Normalized product object
     * @param {string} config.vendor - "shopify" or "nosto"
     * @param {number} config.index - Position in the product list
     * @param {Object} config.gridConfig - Grid configuration (context, id, gridId, settings)
     * @param {Object} [config.cardPayload] - Pre-built CardPayload for priority cards
     * @param {string} [config.injectedMarkup] - Pre-built HTML for priority cards
     */
    arm(config) {
      const { product, vendor, index, gridConfig, cardPayload, injectedMarkup } = config;

      const defaultVariantId = product.defaultVariantId || null;
      const productUrl = `${product.url}${defaultVariantId ? `?variant=${defaultVariantId}` : ""}`;
      const fetchUrl = this.buildArmFetchUrl(product, defaultVariantId);

      const componentId = gridConfig.context === "collection"
        ? `collection-${gridConfig.id}-product-card-${product.id}`
        : gridConfig.context === "search"
          ? `search-product-card-${product.id}`
          : `product-grid-product-card-${product.id}`;

      const sectionId = gridConfig.gridId || gridConfig.id || "product-grid";

      this.id = componentId;
      this.dataset.productId = product.id;
      this.dataset.fetchUrl = fetchUrl;
      this.dataset.productUrl = productUrl;
      this.dataset.defaultVariantId = defaultVariantId;
      this.dataset.productFormId = `quick-add-${sectionId}${product.id}`;
      this.dataset.vendor = vendor;

      if (vendor === "nosto") {
        this.dataset.nostoContext = gridConfig.context === "search"
          ? "product-grid-search"
          : "product-grid-collection";
      }

      // Sold-out state
      if (
        product.tags?.includes("shopify:unavailable") &&
        product.tags?.includes("greyson:available") &&
        product.tags?.includes("ignore-oos-logic")
      ) {
        if (gridConfig.settings?.allowSoldOutStateProducts) {
          this.dataset.showSoldOutState = "true";
        } else {
          this.removeFromDOM("sold-out product excluded by grid settings");
          return;
        }
      }

      if (cardPayload && injectedMarkup) {
        this.dataset.cardPayload = JSON.stringify(cardPayload);
        this.dataset.priorityMode = "true";
        this.dataset.priorityIndex = String(index);
        // Replace skeleton content with priority shell markup
        this.replaceChildren();
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = injectedMarkup;
        while (tempDiv.firstChild) {
          this.appendChild(tempDiv.firstChild);
        }
      }

      this.classList.remove("product-card--skeleton");

      // Re-read constructor values that depend on data attributes
      this.defaultVariantId = defaultVariantId ? parseInt(defaultVariantId, 10) : null;
      this.showSoldOutState = this.dataset.showSoldOutState
        ? stringToBoolean(this.dataset.showSoldOutState)
        : false;

      this.init();
    }

    buildArmFetchUrl(product, defaultVariantId) {
      const url = new URL(product.url, window.location.origin);
      if (defaultVariantId) {
        url.searchParams.set("variant", defaultVariantId);
      }
      url.searchParams.set("view", "product-json");
      return url.toString();
    }

    /**
     * Initializes the product card component.
     *
     * Render-then-fetch: when the grid injected a complete CardPayload + visible
     * body, paint nothing more here — wire the preview and DEFER the per-card
     * product-json fetch until idle or first interaction. Without a payload
     * (e.g. components that reuse this element without the grid pipeline), fall
     * back to the original fetch-first hydration immediately.
     */
    async init() {
      if (this.allowDebug()) console.log("product-card.js: init()", this);

      if (this.isServerRendered()) {
        await this.hydrateServerRenderedCard();
        return;
      }

      this.cardPayload = this.parseCardPayload();
      if (this.cardPayload) {
        this.renderFromCardPayload(this.cardPayload);
        this.scheduleDeferredHydration();
        if (this.allowDebug())
          console.log("product-card.js: init() preview rendered, hydration deferred", this);
        return;
      }

      // No injected payload: hydrate immediately (legacy / non-grid usage).
      await this.hydrateFromFetch();
    }

    async hydrateServerRenderedCard() {
      try {
        this.product = await this.setProduct();
        if (!this.product) {
          console.warn(
            `[product-card] SSR hydration aborted "${this.dataset.productUrl || this.id || "?"}" — product data is null (removal logged above by fetchProduct)`,
            { fetchUrl: this.dataset.fetchUrl || null, element: this },
          );
          return;
        }

        this.variant = this.setDefaultVariant();
        if (!this.variant) {
          console.warn(
            `[product-card] SSR hydration aborted "${this.product?.handle || this.id || "?"}" — no usable variant, fallback exhausted (removal scheduled by setDefaultVariant)`,
            { fetchUrl: this.dataset.fetchUrl || null, element: this },
          );
          return;
        }

        this.hydrateServerRenderedCardContent();

        if (this.allowDebug()) {
          console.log("product-card.js: hydrateServerRenderedCard() finished.", this.product, this);
        }
      } catch (error) {
        console.error("Error hydrating server-rendered product card:", error, this);
        if (this.isConnected) {
          this.removeFromDOM(`SSR hydration error: ${error.message}`);
        }
      }
    }

    /**
     * Full hydration: fetch the per-card product-json, resolve the default /
     * sale / spotlight variant, build templates, and render. This is the path
     * the deferred scheduler runs once interaction/idle fires (or that init()
     * runs directly when no CardPayload was injected).
     */
    async hydrateFromFetch() {
      try {
        // PLP LCP: keep the per-card product-json fetch (and the spotlight/sale
        // variant fetch chains it triggers) off the LCP path. The shell image is
        // already painted and clickable; wait for it to load/paint (bounded)
        // before starting hydration work.
        if (this.shouldDeferPriorityHydration()) {
          await this.waitForPriorityShellPaint();
          // Card may have been removed while waiting (filter/sort re-render).
          if (!this.isConnected) {
            console.warn(
              `[product-card] already gone "${this.dataset.productUrl || this.id || "?"}" — disconnected during deferred hydration wait (removed by an external re-render, not by this card)`,
              {
                productId: this.dataset.productId || null,
                fetchUrl: this.dataset.fetchUrl || null,
                element: this,
              },
            );
            return;
          }
        }
        // set product data
        this.product = await this.setProduct();
        // If product is null (fetch failed), the component should have been removed
        // Exit early to prevent further initialization
        if (!this.product) {
          console.warn(
            `[product-card] hydration aborted "${this.dataset.productUrl || this.id || "?"}" — product data is null (removal logged above by fetchProduct)`,
            { fetchUrl: this.dataset.fetchUrl || null, element: this },
          );
          return;
        }
        if (this.allowDebug())
          console.log("product-card.js product:", this.product);
        // set default variant
        this.variant = this.setDefaultVariant();
        // If variant is null, the component should be removed (setDefaultVariant handles this)
        // Exit early to prevent further initialization
        if (!this.variant) {
          console.warn(
            `[product-card] hydration aborted "${this.product?.handle || this.id || "?"}" — no usable variant, fallback exhausted (removal scheduled by setDefaultVariant)`,
            { fetchUrl: this.dataset.fetchUrl || null, element: this },
          );
          return;
        }
        await this.resolvePrioritizedSaleVariantSelection();
        // Spotlight matching now resolves through swatch option_value_id fetches (extended variants-safe)
        await this.resolveSpotlightVariantSelection();
        if (this.allowDebug())
          console.log("product-card.js variant:", this.variant);
        // set template elements HTML
        this.setTemplateHTML();

        this.hydrateCardContent();
        // Apply any swatch the user selected during the preview (before the
        // full product-json arrived) now that real swatch data exists.
        this.applyPendingSwatchSelection();
        this.classList.remove("product-card--preview");
        // log the initialization
        if (this.allowDebug())
          console.log("product-card.js: hydrateFromFetch() finished.", this.product, this);
      } catch (error) {
        console.error("Error initializing component:", error, this);
        if (this.isConnected) {
          this.removeFromDOM(`hydration error: ${error.message}`);
        }
      }
    }

    /**
     * Apply a swatch selected during the render-then-fetch preview window. Match
     * by option_value_id first, then color handle (Nosto preview swatches carry
     * no option_value_id). Clicks the real swatch input so the full swatch /
     * media swap logic runs. No-op when nothing was pending.
     */
    applyPendingSwatchSelection() {
      const pending = this.pendingSwatchSelection;
      this.pendingSwatchSelection = null;
      if (!pending) return;

      const container = this.querySelector(".product-card-swatches__container");
      if (!container) return;

      let target = null;
      if (pending.optionValueId) {
        target = container.querySelector(
          `.product__option-swatch[data-option-value-id="${pending.optionValueId}"]`,
        );
      }
      if (!target && pending.color) {
        target = container.querySelector(
          `.product__option-swatch[data-color="${pending.color}"]`,
        );
      }
      if (target && !target.checked) {
        target.checked = true;
        target.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    hydrateCardContent() {
      // render the client side card
      this.renderClientSide();
      this.finalizeHydratedCardContent();
    }

    hydrateServerRenderedCardContent() {
      this.finalizeHydratedCardContent();
    }

    finalizeHydratedCardContent() {
      // set content element variables
      this.setContentElements();
      // capture the server-rendered price markup before any discount is applied, so a
      // promotion that later stops applying can restore it
      if (this.elements.priceElement && !this.lastPriceHtml) {
        this.lastPriceHtml = this.elements.priceElement.innerHTML;
      }
      // apply any promotion already resolved before the price element was cached
      this.applyPromotionToPrice();
      // set event listeners
      this.setEventListeners();
      // set default swatch data from the rendered card + variant
      this.setDefaultSwatchData();
      // Preserve the initial media node on first hydrate when it points to
      // the same default-swatch asset to avoid LCP resets from DOM replacement.
      this.setProductImagesFromSwatch(this.selectedSwatchData, {
        preserveInitialMedia: true,
      });
      // When color-only landing is enabled, normalize all card links now that the
      // default swatch is known so the very first render points to the color via
      // partial hydration, not just after a swatch interaction. Gated so the
      // default (setting off) render path is unchanged.
      if (window.theme?.settings?.pdp_require_option_selection === true) {
        this.setProductLinks(this.variant?.url || this.product?.url);
      }
      // inject quickshop
      this.injectQuickShop();
      // setup rewards text
      this.renderCardRewardsText(this);
      // set observers
      this.setObservers();
      // add the initialized class
      this.classList.add("product-card--initialized");
      // notify listeners that product + variant data are now available
      this.dispatchEvent(new CustomEvent("product-card:initialized", {
        bubbles: true,
        detail: { product: this.product, variant: this.variant },
      }));
    }

    /**
     * Sets the default variant for the product card
     * Prioritizes default variant ID from the product data
     * @returns {Object|null} The default variant object or null if not found
     */
    setDefaultVariant() {
      // Early exit conditions
      if (!this.product) {
        if (this.allowDebug())
          console.error(
            "product-card.js: setDefaultVariant() failed, no product found.",
            this,
          );
        return null;
      }

      if (!this.product.available) {
        if (this.allowDebug()) {
          console.log(
            "product-card.js: setDefaultVariant() - Product is not available, removing from DOM",
            this.product,
          );
        }
        setTimeout(() => this.removeFromDOM("product not available"), 0);
        return null;
      }

      if (this.allowDebug()) {
        console.log("product-card.js: setDefaultVariant()", {
          defaultVariantId: this.defaultVariantId,
          defaultVariantIdFromProduct:
            this.product.selected_variant?.id || null,
        });
      }

      // Extended variants: selected variant must come from product-json (which is fetched with ?variant=... when needed)
      const selectedVariant = this.product.selected_variant ?? null;
      if (selectedVariant?.id) {
        return selectedVariant;
      }

      // selected_variant is null when the requested ?variant= id is not published
      // to the Online Store channel. Nosto's feed returns variants regardless of
      // publishing, so the grid can hand us an id Liquid cannot see; Shopify
      // serves a 200 and silently ignores the unresolvable ?variant=, making this
      // the first place the problem surfaces.
      //
      // Recover with a variant Liquid *can* see rather than dropping a card whose
      // other colors are perfectly buyable. Both fallbacks are Liquid-authored, so
      // anything they return is publishable by construction.
      const fallbackVariant =
        this.product.first_greyson_available_variant ??
        this.getFirstAvailableSwatchVariant();

      if (fallbackVariant?.id) {
        console.warn(
          `[product-card] variant fallback "${this.product.handle || this.id || "?"}" — requested variant not published to Online Store, substituting a published variant`,
          {
            handle: this.product.handle || null,
            requestedVariantId: this.defaultVariantId || null,
            fallbackVariantId: fallbackVariant.id,
            fallbackVariantTitle: fallbackVariant.title || null,
            fallbackVariantColor: fallbackVariant.color || null,
            fallbackAvailable: fallbackVariant.available,
            fallbackSource: this.product.first_greyson_available_variant
              ? "first_greyson_available_variant"
              : "swatches",
            vendor: this.dataset.vendor || null,
            fetchUrl: this.dataset.fetchUrl || null,
            element: this,
          },
        );
        return fallbackVariant;
      }

      setTimeout(
        () =>
          this.removeFromDOM(
            "no published variant available (selected_variant, first_greyson_available_variant and swatches all empty)",
          ),
        0,
      );
      return null;
    }

    /**
     * Last-resort variant recovery for unpublished-variant cards.
     *
     * `product.swatches` is built by product-json.liquid from
     * `given_product.options_with_values`, so every `variant_id` it carries is
     * visible to the Online Store by construction — unlike ids sourced from the
     * Nosto feed. Returns a minimal variant-shaped object (the keys the card and
     * quick-add actually read) for the first available swatch, or null.
     * @returns {Object|null}
     */
    getFirstAvailableSwatchVariant() {
      const swatches = Array.isArray(this.product?.swatches)
        ? this.product.swatches
        : [];
      if (swatches.length === 0) return null;

      const swatch =
        swatches.find(
          (candidate) =>
            candidate?.available !== false &&
            (candidate?.variant_id || candidate?.variant),
        ) || null;
      if (!swatch) return null;

      const variantId = swatch.variant_id || swatch.variant;
      if (!variantId) return null;

      return {
        id: variantId,
        url: swatch.url || this.product?.url || null,
        title: swatch.name || swatch.color || "",
        color: swatch.color || "",
        color_family: swatch.color_family || "",
        available: swatch.available !== false,
        on_sale: swatch.on_sale === true,
        on_final_sale: swatch.on_final_sale === true,
        price_html: swatch.price_html || null,
        featured_media_url: swatch.swatch_image || null,
        options: swatch.option_value_id ? [swatch.option_value_id] : [],
      };
    }

    /**
     * Fetches and parses the product data from the JSON script tag
     * @returns {Object} The parsed product data
     */
    async setProduct() {
      // return the product data
      return await this.fetchProduct();
    }

    /**
     * Fetches and parses the product data from the JSON script tag
     * @returns {Object} The parsed product data
     */
    async fetchProduct() {
      try {
        const response = await fetch(this.dataset.fetchUrl);

        // Check if the response is ok (status 200-299)
        if (!response.ok) {
          if (response.status === 404) {
            console.warn(
              `Product not found (404): ${this.dataset.fetchUrl}. Removing product card from DOM.`,
              this,
            );
          } else {
            console.warn(
              `Failed to fetch product (${response.status}): ${this.dataset.fetchUrl}. Removing product card from DOM.`,
              this,
            );
          }

          this.removeFromDOM(`fetch failed (${response.status}): ${this.dataset.fetchUrl}`);
          return null;
        }

        const data = await response.json();

        return data;
      } catch (error) {
        console.warn(
          `Error fetching product data: ${error.message}. Removing product card from DOM.`,
          this,
        );
        this.removeFromDOM(`fetch error: ${error.message}`);
        return null;
      }
    }

    getVariantUrl() {
      return (
        this.variant?.url ||
        this.product?.url ||
        this.dataset.productUrl ||
        "#"
      );
    }

    getActiveProductUrl() {
      return (
        this.selectedSwatchData?.url ||
        this.product?.url ||
        this.dataset.productUrl ||
        window.location.pathname
      );
    }

    getOptionMetaByValueId(optionValueId) {
      if (!optionValueId || !Array.isArray(this.product?.options)) return null;
      for (const option of this.product.options) {
        if (!Array.isArray(option?.values)) continue;
        const match = option.values.find(
          (value) => String(value.id) === String(optionValueId),
        );
        if (match) {
          const position = Number(option.position) || null;
          return {
            name: option.name || "",
            position,
            key: position ? `option${position}` : "",
          };
        }
      }
      return null;
    }

    getColorOptionMeta() {
      const swatches = Array.isArray(this.product?.swatches)
        ? this.product.swatches
        : [];
      for (const swatch of swatches) {
        const meta = this.getOptionMetaByValueId(swatch.option_value_id);
        if (meta) return meta;
      }

      const fallbackOptions = Array.isArray(this.product?.options)
        ? this.product.options
        : [];
      const fallbackColorOption = fallbackOptions.find((option) => {
        const name = String(option?.name || "").toLowerCase();
        return name.includes("color");
      });
      if (fallbackColorOption) {
        const position = Number(fallbackColorOption.position) || 1;
        return {
          name: fallbackColorOption.name || "Color",
          position,
          key: `option${position}`,
        };
      }
      return { name: "Color", position: 1, key: "option1" };
    }

    getOptionValueById(optionValueId) {
      if (!optionValueId || !Array.isArray(this.product?.options)) return null;
      for (const option of this.product.options) {
        if (!Array.isArray(option?.values)) continue;
        const match = option.values.find(
          (value) => String(value.id) === String(optionValueId),
        );
        if (match) return match;
      }
      return null;
    }

    getSelectedVariantOptionValueIds() {
      const optionIds = Array.isArray(this.variant?.options)
        ? this.variant.options
        : [];
      const normalizedIds = optionIds
        .map((value) => String(value || "").trim())
        .filter(Boolean);
      return new Set(normalizedIds);
    }

    getSelectedVariantSwatch() {
      const swatches = Array.isArray(this.product?.swatches)
        ? this.product.swatches
        : [];
      if (swatches.length === 0) return null;

      const selectedOptionValueIds = this.getSelectedVariantOptionValueIds();
      if (selectedOptionValueIds.size > 0) {
        const optionIdMatch = swatches.find((swatch) =>
          selectedOptionValueIds.has(String(swatch?.option_value_id || "").trim()),
        );
        if (optionIdMatch) return optionIdMatch;
      }

      const selectedVariantId = String(this.variant?.id || "").trim();
      if (selectedVariantId) {
        const variantIdMatch = swatches.find(
          (swatch) => String(swatch?.variant_id || swatch?.variant || "").trim() === selectedVariantId,
        );
        if (variantIdMatch) return variantIdMatch;
      }

      const selectedVariantColor = handleize(String(this.variant?.color || ""));
      if (selectedVariantColor) {
        return (
          swatches.find((swatch) => {
            const swatchColor = handleize(String(swatch?.color || swatch?.name || ""));
            return swatchColor === selectedVariantColor;
          }) || null
        );
      }

      return null;
    }

    getNormalizedColorSet(colors = []) {
      if (!Array.isArray(colors) || colors.length === 0) return new Set();
      return new Set(
        colors
          .map((color) => handleize(String(color || "")))
          .filter(Boolean),
      );
    }

    getSaleColorSet() {
      return this.getNormalizedColorSet(this.product?.sale_colors);
    }

    isSaleSwatch(swatch) {
      if (!swatch) return false;

      const swatchColor = handleize(String(swatch.color || swatch.name || ""));
      if (!swatchColor) return false;

      const saleColorSet = this.getSaleColorSet();
      if (saleColorSet.size > 0) {
        return saleColorSet.has(swatchColor);
      }

      return swatch.isSale === true;
    }

    isSpotlightSwatchEligible(swatch) {
      if (!swatch || swatch.available === false) return false;

      const swatchColor = handleize(String(swatch.color || swatch.name || ""));
      if (!swatchColor) return false;

      const availableColorSet = this.getNormalizedColorSet(
        this.product?.available_colors,
      );
      const hiddenColorSet = this.getNormalizedColorSet(this.product?.hide_colors);
      const unavailableColorSet = this.getNormalizedColorSet(
        this.product?.unavailable_colors,
      );

      if (availableColorSet.size > 0 && !availableColorSet.has(swatchColor)) {
        return false;
      }
      if (hiddenColorSet.has(swatchColor) || unavailableColorSet.has(swatchColor)) {
        return false;
      }

      const optionValue = this.getOptionValueById(swatch.option_value_id);
      if (optionValue && optionValue.available === false) return false;

      return true;
    }

    async fetchVariantData(optionValueIds = [], baseUrl = null) {
      if (this.allowDebug()) {
        console.log("product-card.js: fetchVariantData()", baseUrl || this.getActiveProductUrl(), optionValueIds);
      }
      return resolveVariant(baseUrl || this.getActiveProductUrl(), optionValueIds);
    }

    async prefetchVariantData(optionValueIds = [], baseUrl = null) {
      if (!Array.isArray(optionValueIds) || optionValueIds.length === 0) return;
      await prefetchVariant(baseUrl || this.getActiveProductUrl(), optionValueIds);
    }

    prefetchSwatchMedia(swatch) {
      const swatchImages =
        Array.isArray(swatch?.media) && swatch.media.length > 0
          ? swatch.media
          : Array.isArray(this.product?.media)
            ? this.product.media
            : [];
      if (!swatchImages.length) return;

      swatchImages.slice(0, 2).forEach((imageTag) => {
        if (typeof imageTag !== "string") return;
        const doc = cardMediaDomParser.parseFromString(imageTag, "text/html");
        const img = doc.querySelector("img");
        const src = img?.getAttribute("src");
        if (!src || this.prefetchedSwatchMediaUrls.has(src)) return;

        this.prefetchedSwatchMediaUrls.add(src);
        const preloadImg = new Image();
        preloadImg.src = src;
      });
    }

    preloadSliderImageAtIndex(sliderImages = [], targetIndex = 0) {
      if (!Array.isArray(sliderImages) || sliderImages.length === 0) return;
      const safeIndex =
        ((targetIndex % sliderImages.length) + sliderImages.length) %
        sliderImages.length;
      const imageTag = sliderImages[safeIndex];
      if (typeof imageTag !== "string") return;

      const doc = cardMediaDomParser.parseFromString(imageTag, "text/html");
      const img = doc.querySelector("img");
      const src = img?.getAttribute("src");
      if (!src || this.prefetchedSwatchMediaUrls.has(src)) return;

      this.prefetchedSwatchMediaUrls.add(src);
      const preloadImg = new Image();
      preloadImg.src = src;
    }

    bindSliderLookaheadPrefetch(sliderImages = [], initialIndex = 0) {
      if (
        !this.slider ||
        !Array.isArray(sliderImages) ||
        sliderImages.length <= 1
      )
        return;

      // Warm the next gallery frame right away.
      this.preloadSliderImageAtIndex(sliderImages, initialIndex + 1);

      this.slider.on("select", () => {
        this.preloadSliderImageAtIndex(
          sliderImages,
          Number(this.slider.selectedScrollSnap()) + 1,
        );
      });
    }

    setEmblaSelectedSlideClass(sliderWrapper) {
      if (!this.slider || !sliderWrapper) return;
      const slides = sliderWrapper.querySelectorAll(".carousel-cell");
      const setSelectedState = () => {
        const selectedIndex = this.slider.selectedScrollSnap();
        slides.forEach((slide, index) => {
          slide.classList.toggle("is-selected", index === selectedIndex);
        });
        this.setEmblaSlideLinkAccessibility(slides, selectedIndex);
      };
      setSelectedState();
      this.slider.on("select", setSelectedState);
      this.slider.on("reInit", setSelectedState);
    }

    setEmblaSlideLinkAccessibility(slides, selectedIndex) {
      if (!slides?.length) return;
      slides.forEach((slide, index) => {
        const slideLink = slide.querySelector(".plp-card-slide-link");
        if (!slideLink) return;
        const isSelected = index === selectedIndex;
        slideLink.setAttribute("tabindex", isSelected ? "0" : "-1");
        slideLink.setAttribute("aria-hidden", isSelected ? "false" : "true");
      });
    }

    setupEmblaMobileCustomPagination(sliderWrapper, swatchImages) {
      if (!this.slider || !isMobile() || swatchImages.length < 4) return;
      const dotsNode = sliderWrapper.querySelector(".embla__dots");
      if (!dotsNode) return;
      if (this.customDotsObserver) {
        this.customDotsObserver.disconnect();
        this.customDotsObserver = null;
      }

      dotsNode.classList.add("custom-pagination");
      const createDots = (index = 0) => {
        dotsNode.replaceChildren();
        const isFourSlides = swatchImages.length === 4;
        const dotsCount = index < 3 ? 3 : isFourSlides ? 1 : 2;
        const activeIndex = index < 3 ? index : isFourSlides ? 0 : index - 3;
        const barOnLeft = index >= 3;
        const dots = Array.from({ length: dotsCount }, (_, i) => {
          const dot = document.createElement("div");
          dot.classList.add("pagination-dot");
          if (i === activeIndex) dot.classList.add("active");
          return dot;
        });
        dotsNode.replaceChildren(...dots);
        const bar = document.createElement("div");
        bar.classList.add("pagination-bar");
        if (barOnLeft) {
          dotsNode.insertBefore(bar, dotsNode.firstChild);
        } else {
          dotsNode.appendChild(bar);
        }
      };

      const renderCustomDots = () =>
        createDots(Number(this.slider.selectedScrollSnap()) || 0);

      // Embla may paint default dot buttons after init/reInit, so enforce custom dots
      // for a few frames until the DOM settles.
      const enforceCustomDots = (attempt = 0) => {
        renderCustomDots();
        const hasDefaultEmblaDots = !!dotsNode.querySelector(".embla__dot");
        if (hasDefaultEmblaDots && attempt < 8) {
          setTimeout(() => enforceCustomDots(attempt + 1), 40);
        }
      };

      enforceCustomDots();

      this.slider.on("select", renderCustomDots);
      this.slider.on("reInit", () => enforceCustomDots());

      this.customDotsObserver = new MutationObserver(() => {
        if (dotsNode.querySelector(".embla__dot")) {
          renderCustomDots();
        }
      });
      this.customDotsObserver.observe(dotsNode, {
        childList: true,
        subtree: true,
      });
    }

    initializeEmblaSliderInstance(
      sliderElement,
      sliderImages = [],
      initialIndex = 0,
      swatchImages = [],
    ) {
      if (!sliderElement) return;
      let attempts = 0;
      const maxAttempts = 10;
      const bindEmblaApi = () => {
        const emblaApi = sliderElement.emblaApi;
        if (!emblaApi) {
          attempts += 1;
          if (attempts <= maxAttempts) {
            setTimeout(bindEmblaApi, 50);
          }
          return;
        }
        this.sliderComponent = sliderElement;
        this.slider = emblaApi;
        sliderElement.classList.add("embla-initialized");
        this.setEmblaSelectedSlideClass(sliderElement);
        this.bindSliderLookaheadPrefetch(sliderImages, initialIndex);
        this.setupEmblaMobileCustomPagination(sliderElement, swatchImages);
      };
      bindEmblaApi();
    }

    shouldUseDesktopHoverBehavior() {
      const hasHoverPointer = window.matchMedia(
        "(hover: hover) and (pointer: fine)",
      ).matches;
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      const isDesktopViewport = window.matchMedia("(min-width: 768px)").matches;
      return hasHoverPointer && !prefersReducedMotion && isDesktopViewport;
    }

    getMediaMode() {
      if (isMobile()) return "mobile-slider";
      if (this.shouldUseDesktopHoverBehavior()) return "desktop-layered";
      return "desktop-slider";
    }

    handleViewportModeChange() {
      if (this.viewportModeRaf) {
        cancelAnimationFrame(this.viewportModeRaf);
      }

      this.viewportModeRaf = requestAnimationFrame(() => {
        const nextMode = this.getMediaMode();
        const modeChanged = this.currentMediaMode !== nextMode;
        this.currentMediaMode = nextMode;

        if (modeChanged && this.selectedSwatchData) {
          this.setProductImagesFromSwatch(this.selectedSwatchData);
          return;
        }

        if (this.slider && typeof this.slider.reInit === "function") {
          this.slider.reInit();
        }
      });
    }

    handleCardMediaHoverEnter() {
      // Render-then-fetch: the gallery slider is built lazily on first hover so
      // hydrate never touches the painted media (no settle flash). Build it now.
      this.ensureSwatchMediaSlider();
      if (!this.slider || !this.shouldUseDesktopHoverBehavior()) return;
      const slideCount = this.slider.slideNodes?.().length || 0;
      if (slideCount <= 1) return;
      const currentIndex = this.slider.selectedScrollSnap?.() ?? 0;
      if (currentIndex !== 0) {
        this.slider.scrollTo(0, true);
      }
    }


    applyVariantData(variantData) {
      if (!variantData) return null;
      this.product.selected_variant =
        variantData.selected_variant ?? this.product.selected_variant;
      this.product.options = variantData.options ?? this.product.options;
      this.product.url = variantData.url ?? this.product.url;
      this.variant = this.product.selected_variant ?? this.variant;
      return this.variant;
    }

    async fetchAndApplyVariantData(optionValueIds = [], baseUrl = null) {
      const variantData = await this.fetchVariantData(optionValueIds, baseUrl);
      if (!variantData) return null;
      this.applyVariantData(variantData);
      return variantData;
    }

    /**
     * Removes the product card from the DOM
     * If there's a parent .grid__item element, removes that instead
     */
    removeFromDOM(reason = "unknown") {
      try {
        const handle = this.product?.handle || this.dataset.productUrl || this.id || "?";
        const parentGridItem = this.closest(".product-grid__item") || this.closest(".grid__item");

        console.warn(`[product-card] removing "${handle}" — ${reason}`, {
          handle,
          reason,
          productId: this.dataset.productId || null,
          productUrl: this.dataset.productUrl || null,
          fetchUrl: this.dataset.fetchUrl || null,
          defaultVariantId: this.dataset.defaultVariantId || null,
          vendor: this.dataset.vendor || null,
          priorityMode: this.dataset.priorityMode === "true",
          priorityIndex: this.dataset.priorityIndex || null,
          serverRendered: this.isServerRendered(),
          hadPayload: !!this.dataset.cardPayload,
          hydrationStarted: this.deferredHydrationStarted,
          initialized: this.classList.contains("product-card--initialized"),
          removed: parentGridItem ? "parent grid item" : "product-card element",
          element: this,
        });

        if (parentGridItem) {
          parentGridItem.remove();
        } else {
          this.remove();
        }
      } catch (error) {
        console.error("Error removing product card from DOM:", error, this);
      }
    }

    /**
     * Finds and stores references to template elements
     * These templates are used for rendering parts of the component
     */
    /**
     * Builds the `<product-promotion>` markup for this card.
     *
     * Shared by both template paths so a card with no resolved variant still fetches
     * its promotion. The fetch URL falls back from the variant to the product, and
     * the endpoint resolves the product's own promotions from that URL.
     *
     * @returns {string} The promotion element markup
     */
    buildPromotionTemplateHTML() {
      return `
        <product-promotion
          class="product-card__promotion lazy-load"
          id="product__promotion--${this.product.handle}"
        >
          <script type="application/json" id="product__promotion--${this.product.handle}__json" component-config>
            {
              "template": "card",
              "fetch": {
                "url": "${this.getVariantUrl()}"
              }
            }
          </script>
          <div class="product__promotion__inner"></div>
        </product-promotion>
      `;
    }

    setTemplateHTML() {
      // Early exit if variant is not available
      if (!this.variant) {
        if (this.allowDebug()) {
          console.log(
            "product-card.js: setTemplateHTML() - No variant available, skipping template setup",
          );
        }

        // Set minimal templates to prevent errors
        this.templates.quickViewTemplateHTML = "";
        this.templates.quickAddToBagTemplateHTML = "";
        // A promotion resolves against the product as well as the variant, so keep
        // the promotion element even with no variant. Colour-only selection leaves a
        // card unresolved by design, and dropping the element here would lose both
        // the promotion message and its price discount.
        this.templates.promotionTemplateHTML = this.buildPromotionTemplateHTML();
        this.templates.componentTemplateHTML = `
          <div class="product-card__inner">
            <div class="product-card__media"></div>
            <div data-slot="media-badges"></div>
          </div>
          <div class="product-card__content">
            <div class="product-card__info">
              <div data-slot="info-badges"></div>
              <div data-slot="title"></div>
              <div data-slot="subtitle"></div>
              <div data-slot="price"></div>
            </div>
            <div data-slot="swatches"></div>
            ${this.product.block_promotions ? "" : this.templates.promotionTemplateHTML}
          </div>
        `;
        return;
      }

      // get the quick view template
      this.templates.quickViewTemplateHTML = `
        <div class="product-card__quick-shop">
          <div class="product-card__quick-add no-js-hidden mobile-hidden">
            <modal-opener data-modal="#QuickAdd-${this.product.id}">
              <button
                id="${this.dataset.productFormId}-submit"
                type="submit"
                name="add"
                class="product-card__quick-view-btn"
                aria-haspopup="dialog"
                aria-labelledby="${this.dataset.productFormId}-submit title-${this.dataset.sectionId}-${this.product.id}"
                data-product-url="${this.product.url || "#"}"
              >
                ${window.theme.language_strings.product_quick_view}
                <div class="loading-overlay__spinner hidden">
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    class="spinner"
                    viewBox="0 0 66 66"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                  </svg>
                </div>
              </button>
            </modal-opener>
            <quick-add-modal id="QuickAdd-${this.product.id}" class="quick-add-modal ${this.allowDebug() ? "debug" : ""}">
              <span class="modal-overlay"></span>
              <div
                role="dialog"
                aria-label="${window.theme.language_strings.product_quick_view_view_details}"
                aria-modal="true"
                class="quick-add-modal__content global-settings-popup"
                tabindex="-1"
              >
                <button
                  id="ModalClose-${this.product.id}"
                  type="button"
                  class="quick-add-modal__toggle"
                  aria-label="${window.theme.language_strings.accessibility_close}"
                >
                  ${window.theme.elements.svg_close}
                </button>
                <div id="QuickAddInfo-${this.product.id}" class="quick-add-modal__content-info"></div>
              </div>
            </quick-add-modal>
          </div>
        </div>
      `;
      // get the quick add to bag template
      this.templates.quickAddToBagTemplateHTML = `
        <div class="product-card__quick-shop">
          <div class="product-card__quick-add no-js-hidden mobile-hidden">
            <quick-add-to-bag
              class="quick-add-to-bag ${this.allowDebug() ? "debug" : ""}"
              data-quick-add-mode="${this.dataset.quickAddMode || window.theme?.settings?.quick_add_mode || "auto"}"
            >
              <div class="quick-add-to-bag__option-select-container"></div>
              <div class="quick-add-to-bag__cart-messages"></div>
            </quick-add-to-bag>
          </div>
        </div>
      `;
      // set the promotion template
      this.templates.promotionTemplateHTML = this.buildPromotionTemplateHTML();
      // set the component base template
      this.templates.componentTemplateHTML = `
        <div class="product-card__inner">
          <div class="product-card__media"></div>
          <div data-slot="media-badges"></div>
        </div>
        <div class="product-card__content">
          <div class="product-card__info">
            <div data-slot="info-badges"></div>
            <div data-slot="title"></div>
            <div data-slot="subtitle"></div>
            <div data-slot="price"></div>
          </div>
          <div data-slot="swatches"></div>
          ${this.product.block_promotions ? "" : this.templates.promotionTemplateHTML}
        </div>
      `;
    }

    /**
     * Finds and stores references to content elements
     * These elements contain the visible content of the component
     */
    setContentElements() {
      // get the price element
      this.elements.priceElement = this.querySelector(
        ".product-card__price .product-card__price-option",
      );
      // get the product promotion
      this.elements.productPromotion = this.querySelector("product-promotion");
      // get the product swatches
      this.elements.productSwatches = this.querySelector(
        ".product-card-swatches__container",
      );
    }

    /**
     * Renders the product card content on the client side
     * Used when client-side rendering mode is enabled
     */
    renderClientSide() {
      if (this.allowDebug())
        console.log("product-card.js: renderClientSide()", this.product);
      // early exit conditions
      if (!this.product || !this.templates.componentTemplateHTML) {
        console.error(
          "Missing data or client template for client-side rendering",
          this,
        );
        return;
      }
      // try to render the client side
      try {
        // Render-then-fetch: EVERY injected card carries data-priority-shell
        // (not just eager/priority cards), so the in-place hydrate — reuse the
        // live <img>, clear+refill the [data-slot] containers — applies to all
        // cards and the upgrade is non-destructive (no replaceChildren reflow).
        const priorityShell = this.querySelector("[data-priority-shell='true']");
        const hydrateInPlace = !!priorityShell;
        let templateHTML = null;

        if (hydrateInPlace) {
          templateHTML = priorityShell;
          templateHTML.removeAttribute("data-priority-shell");
          // A server-rendered card already ships a <product-promotion>. Keep that one
          // and drop any duplicate, so the card holds exactly one promotion element.
          const existingPromotions = this.querySelectorAll("product-promotion");
          for (let i = 1; i < existingPromotions.length; i += 1) {
            existingPromotions[i].remove();
          }
          // Cards injected by product-grid.js carry a priority shell with no promotion
          // element, and this branch never applies componentTemplateHTML, so add it
          // here. Without it the card resolves no promotion, and therefore no
          // promotional price.
          if (
            existingPromotions.length === 0 &&
            !this.product.block_promotions &&
            this.templates.promotionTemplateHTML
          ) {
            const cardContent = this.querySelector(".product-card__content");
            if (cardContent) {
              cardContent.appendChild(
                createElementFromHTML(this.templates.promotionTemplateHTML),
              );
            }
          }
          // Media-badges slot lives inside .product-card__inner (the shell).
          this.clearSlot(templateHTML, "[data-slot='media-badges']");
          templateHTML.querySelector(".product-card__media-badges")?.remove();
          templateHTML.querySelector(".product-card__badges")?.remove();
          templateHTML.querySelector(".members-messaging")?.remove();
          // Content-area slots (title, price, swatches) are siblings of the
          // shell inside <product-card>, so clear them on `this`.
          this.clearSlot(this, "[data-slot='info-badges']");
          this.clearSlot(this, "[data-slot='title']");
          this.clearSlot(this, "[data-slot='subtitle']");
          this.clearSlot(this, "[data-slot='price']");
          this.clearSlot(this, "[data-slot='swatches']");
        } else {
          this.replaceChildren();
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = this.templates.componentTemplateHTML.trim();
          while (tempDiv.firstElementChild) {
            this.appendChild(tempDiv.firstElementChild);
          }
          templateHTML = this.querySelector(".product-card__inner");
        }
        // For hydrate-in-place, content-area renders need `this` as container
        // so they can find [data-slot] elements in .product-card__content.
        const contentContainer = this;
        this.renderClientSideCardMedia(templateHTML);
        this.renderClientSideCardMediaBadges(templateHTML);
        this.renderClientSideCardTitle(contentContainer);
        this.renderClientSideCardPrice(contentContainer);
        this.renderClientSideCardInfoBadges(contentContainer);
        this.renderClientSideCardSwatches(contentContainer);

        if (this.allowDebug())
          console.log(
            "product-card.js: renderClientSide() finished",
            templateHTML,
          );
      } catch (error) {
        console.error("Error rendering client-side:", error, this);
      }
    }

    /**
     * Renders the media overlaybadges portion of the product card
     * @param {HTMLElement} container - The container element to render into
     */
    renderClientSideCardMediaBadges(container) {
      const mediaBadgesContainer =
        this.clearSlot(container, "[data-slot='media-badges']") ||
        container.querySelector(".product-card__inner");
      if (!mediaBadgesContainer) return;

      // Render membership messaging if applicable
      const productMembership = this.product.membership || null;
      const productMembershipRequired =
        productMembership?.loyalty_required || false;
      const productMembershipGroup =
        productMembership?.membership_group_required || null;
      const productMembershipGroupHandle = productMembershipRequired
        ? String(handleize(productMembershipGroup?.title) || "default").replace(
            "-",
            "_",
          )
        : "";

      if (productMembershipGroupHandle.length > 0) {
        const membershipIcon =
          window.theme.elements.membership_icons[productMembershipGroupHandle] ||
          window.theme.elements.membership_icons.default ||
          "";
        const membershipBadgeTemplateHTML = `
          <div class="members-messaging">
            <div class="members-messaging--icon">
              ${membershipIcon}
            </div>
          </div>
        `;
        mediaBadgesContainer.appendChild(
          createElementFromHTML(membershipBadgeTemplateHTML),
        );
      }

      // Render filtered badges for image label location
      this.renderBadges(mediaBadgesContainer, "Image label", "product-card__media-badges");
    }

    /**
     * Gets collection data from DOM instead of making fetch calls
     * @returns {Object|null} The collection data or null if not found
     */
    getProductGridConfig() {
      const closestProductGrid = this.closest("product-grid");
      return closestProductGrid?.config || null;
    }

    /**
     * Check if ProductGrid is filtered or sorted
     * @returns {boolean} True if ProductGrid has data-filtered="true" or product-grid--filtered class
     * @private
     */
    isProductGridFiltered() {
      const productGrid = this.closest("product-grid");
      if (!productGrid) return false;

      return (
        (productGrid.hasAttribute("data-filtered") &&
          productGrid.getAttribute("data-filtered") === "true") ||
        productGrid.classList.contains("product-grid--filtered")
      );
    }

    /**
     * Check if ProductGrid is price sorted
     * @returns {boolean} True if ProductGrid is sorted by price
     * @private
     */
    isProductGridPriceSorted() {
      const productGridConfig = this.getProductGridConfig();
      if (!productGridConfig) return false;

      return (
        productGridConfig.sortBy === "price-ascending" ||
        productGridConfig.sortBy === "price-descending"
      );
    }

    /**
     * Check if ProductGrid is using a non-default sort option
     * @returns {boolean} True if sortBy differs from defaultSortBy
     * @private
     */
    isProductGridUsingNonDefaultSort() {
      const productGridConfig = this.getProductGridConfig();
      if (!productGridConfig) return false;

      const defaultSortBy =
        productGridConfig.defaultSortBy ||
        (productGridConfig.context === "collection" ? "manual" : "relevance");
      const currentSortBy = productGridConfig.sortBy || defaultSortBy;

      return currentSortBy !== defaultSortBy;
    }

    /**
     * Determine if sale swatches should be prioritized
     * Only applies when:
     * - ProductGrid exists and context is "collection"
     * - prioritizeSaleVariants setting is enabled
     * - ProductGrid is not filtered and not using non-default sort
     * @returns {boolean}
     * @private
     */
    shouldPrioritizeSaleVariants() {
      const productGridConfig = this.getProductGridConfig();
      if (!productGridConfig) return false;
      if (productGridConfig.context !== "collection") return false;
      if (productGridConfig.settings?.prioritizeSaleVariants !== true) return false;
      if (this.isProductGridFiltered()) return false;
      if (this.isProductGridUsingNonDefaultSort()) return false;

      return true;
    }

    /**
     * Return sale swatches in product order for top slots
     * @param {number} maxSwatches
     * @returns {Array}
     * @private
     */
    findPrioritizedSaleSwatches(maxSwatches = 3) {
      const swatches = Array.isArray(this.product?.swatches)
        ? this.product.swatches
        : [];
      if (swatches.length === 0) return [];

      const availableColorSet = this.getNormalizedColorSet(
        this.product?.available_colors,
      );
      const seen = new Set();
      const saleSwatches = [];

      for (const swatch of swatches) {
        if (!swatch) continue;
        if (swatch.available === false) continue;
        if (!this.isSaleSwatch(swatch)) continue;
        const normalizedSwatchColor = handleize(
          String(swatch.color || swatch.name || ""),
        );
        if (availableColorSet.size > 0 && !availableColorSet.has(normalizedSwatchColor))
          continue;

        const dedupeKey = String(swatch.option_value_id || swatch.handle || swatch.color);
        if (seen.has(dedupeKey)) continue;

        seen.add(dedupeKey);
        saleSwatches.push(swatch);

        if (saleSwatches.length >= maxSwatches) break;
      }

      return saleSwatches;
    }

    /**
     * Determine if spotlight variant selection should be used
     * Only applies when:
     * - ProductGrid exists and context is "collection"
     * - ProductGrid is NOT filtered or price sorted
     * - Spotlight colors are configured
     * @returns {boolean} True if spotlight variant selection should be used
     * @private
     */
    shouldUseSpotlightVariantSelection() {
      const productGridConfig = this.getProductGridConfig();

      // Must have ProductGrid config
      if (!productGridConfig) return false;

      if (this.shouldPrioritizeSaleVariants()) return false;

      // Must be collection context
      if (productGridConfig.context !== "collection") return false;

      // Must NOT be filtered or price sorted
      if (this.isProductGridFiltered() || this.isProductGridPriceSorted())
        return false;

      // Must have spotlight colors configured
      const hasSpotlightColors =
        productGridConfig.settings?.colorSpotlightColors &&
        Array.isArray(productGridConfig.settings.colorSpotlightColors) &&
        productGridConfig.settings.colorSpotlightColors.length > 0;
      const hasSpotlightColorFamily =
        productGridConfig.settings?.colorSpotlightColorFamily;

      if (!hasSpotlightColors && !hasSpotlightColorFamily) return false;

      return true;
    }

    async resolvePrioritizedSaleVariantSelection() {
      if (!this.shouldPrioritizeSaleVariants()) return;

      const prioritizedSwatches = this.findPrioritizedSaleSwatches(1);
      const prioritizedSwatch = prioritizedSwatches[0];
      if (!prioritizedSwatch) return;

      const optionValueIds = prioritizedSwatch.option_value_id
        ? [String(prioritizedSwatch.option_value_id)]
        : [];
      if (optionValueIds.length === 0) return;

      const variantData = await this.fetchVariantData(
        optionValueIds,
        prioritizedSwatch.url || this.product?.url,
      );
      if (!variantData?.selected_variant) return;

      this.applyVariantData(variantData);
      this.selectedSwatchData = prioritizedSwatch;
    }

    findSpotlightSwatch() {
      const productGridConfig = this.getProductGridConfig();
      const swatches = Array.isArray(this.product?.swatches)
        ? this.product.swatches
        : [];
      if (!productGridConfig || swatches.length === 0) return null;

      const spotlightColors = productGridConfig.settings?.colorSpotlightColors;
      const spotlightColorFamily =
        productGridConfig.settings?.colorSpotlightColorFamily;

      const normalizedSpotlightColors =
        Array.isArray(spotlightColors) && spotlightColors.length > 0
          ? spotlightColors.map((color) => handleize(color))
          : [];

      if (normalizedSpotlightColors.length > 0) {
        for (const spotlightColor of normalizedSpotlightColors) {
          const matchingSwatch = swatches.find((swatch) => {
            if (!this.isSpotlightSwatchEligible(swatch)) return false;
            const normalizedSwatchColor = swatch.color
              ? handleize(swatch.color)
              : null;
            return normalizedSwatchColor === spotlightColor;
          });
          if (matchingSwatch) {
            return matchingSwatch;
          }
        }
      }

      if (spotlightColorFamily) {
        const normalizedFamily = handleize(spotlightColorFamily);
        const familySwatch = swatches.find((swatch) => {
          if (!this.isSpotlightSwatchEligible(swatch)) return false;
          return handleize(swatch.color_family || "") === normalizedFamily;
        });
        if (familySwatch) {
          return familySwatch;
        }
      }

      return null;
    }

    async resolveSpotlightVariantSelection() {
      if (!this.shouldUseSpotlightVariantSelection()) return;

      const spotlightSwatch = this.findSpotlightSwatch();
      if (!spotlightSwatch) return;

      const optionValueIds = spotlightSwatch.option_value_id
        ? [String(spotlightSwatch.option_value_id)]
        : [];
      if (optionValueIds.length === 0) return;

      const variantData = await this.fetchVariantData(
        optionValueIds,
        spotlightSwatch.url || this.product?.url,
      );
      if (!variantData?.selected_variant) return;

      const selectedVariantColor = handleize(
        String(variantData.selected_variant.color || ""),
      );
      const spotlightColor = handleize(
        String(spotlightSwatch.color || spotlightSwatch.name || ""),
      );

      if (selectedVariantColor && spotlightColor && selectedVariantColor !== spotlightColor) {
        if (this.allowDebug()) {
          console.warn(
            "product-card.js: resolveSpotlightVariantSelection() skipped variant apply due to color mismatch",
            {
              spotlightSwatch,
              selectedVariant: variantData.selected_variant,
            },
          );
        }
        return;
      }

      this.applyVariantData(variantData);
      this.selectedSwatchData = spotlightSwatch;
      if (this.allowDebug()) {
        console.log(
          "product-card.js: resolveSpotlightVariantSelection() selected swatch/variant",
          {
            swatch: spotlightSwatch,
            variant: this.variant,
          },
        );
      }
    }

    /**
     * Checks if a badge should be shown based on collection filtering rules
     * @param {Object} badge - The badge object to check
     * @param {Object} productGridConfig - The product grid config
     * @returns {boolean} Whether the badge should be shown
     */
    shouldShowBadge(badge, productGridConfig) {
      const collectionConfig = productGridConfig || {};
      const rejectedBadges = collectionConfig.rejectedBadges || [];
      const exclusiveBadges = collectionConfig.exclusiveBadges || [];
      const collectionId = Number(collectionConfig.id);
      const isCollectionContext =
        collectionConfig.context === "collection" &&
        Number.isFinite(collectionId);
      const hasExclusiveCollections =
        Array.isArray(badge.exclusive_collections) &&
        badge.exclusive_collections.length > 0;

      if (hasExclusiveCollections && !isCollectionContext) return false;

      // Check if badge is rejected by collection
      const isRejectedByCollection =
        Array.isArray(rejectedBadges) &&
        rejectedBadges.some(
          (rejectedBadge) => rejectedBadge.label === badge.label,
        );

      // Check if badge is exclusive by collection
      const isExclusiveByCollection =
        Array.isArray(exclusiveBadges) &&
        exclusiveBadges.some(
          (exclusiveBadge) => exclusiveBadge.label === badge.label,
        );

      // If rejected by collection, don't show
      if (isRejectedByCollection) return false;

      // If exclusive by collection but not in exclusive list, don't show
      if (exclusiveBadges.length > 0 && !isExclusiveByCollection) return false;

      // Check badge's exclusive collections
      if (hasExclusiveCollections) {
        if (!badge.exclusive_collections.includes(collectionId)) {
          return false;
        }
      }

      // Check badge's rejected collections
      if (badge.rejected_collections?.length > 0) {
        if (badge.rejected_collections.includes(collectionId)) {
          return false;
        }
      }

      return true;
    }

    /**
     * Filters badges based on location and collection rules
     * @param {string} targetLocation - The target location ("Image label" or "Card info")
     * @returns {string} HTML string of filtered badges
     */
    getFilteredBadgesHTML(targetLocation) {
      if (this.showSoldOutState && targetLocation == "Image label") {
        return `
          <span
            class="badge badge-new"
            style="
              background-color: ${window.theme.colors.sold_out_badge_background_color};
              color: ${window.theme.colors.sold_out_badge_text_color}
            "
          >
            ${window.theme.language_strings.variant_sold_out}
          </span>
        `;
      }

      const productBadges = this.product.badges || [];
      if (productBadges.length === 0) return "";

      const productGridConfig = this.getProductGridConfig();

      return productBadges
        .filter((badge) => {
          const shouldShow = this.shouldShowBadge(badge, productGridConfig);
          const badgeLocation = badge.card_location || "Image label";
          return shouldShow && badgeLocation === targetLocation;
        })
        .map((badge) => badge.html)
        .join("");
    }

    /**
     * Renders the media (images) portion of the product card
     * @param {HTMLElement} container - The container element to render into
     */
    renderClientSideCardMedia(container) {
      // get the media container
      const mediaContainer = container.querySelector(".product-card__media");
      // early exit conditions
      if (!mediaContainer) return;
      if (this.allowDebug())
        console.log(
          "product-card.js: renderClientSideCardMedia()",
          this.product,
        );

      // Render-then-fetch: when the grid injected a complete card body, the
      // media <img> is already final (correct src/srcset/sizes for the default
      // swatch). Touching it here (rewriting src/attrs, dropping the inline
      // style, appending a hover image) caused a visible repaint/"flash" on
      // settle even though the image is identical. Leave it ALONE; the gallery
      // slider + hover image are built lazily on first interaction
      // (ensureSwatchMediaSlider). Skip only when the painted asset still
      // matches the selected swatch (else fall through to a normal rebuild).
      if (this.shouldPreserveInjectedMedia(mediaContainer)) {
        const swatchMedia =
          this.getSelectedVariantSwatch()?.media || this.product.media || [];
        this.deferredMediaSliderPending = swatchMedia.length > 1;
        return;
      }

      // Get media from data
      // Primary product card media should come from the selected variant swatch first.
      // Swatch media is a string array of image tag HTML.
      let media = [];
      const selectedVariantSwatch = this.getSelectedVariantSwatch();
      if (selectedVariantSwatch) media = selectedVariantSwatch.media || [];

      // if we cannot find swatch media, use the product media
      if (!media || media.length === 0) {
        media = this.product.media || [];
      }
      // final fallback for reduced payloads: build a single image from swatch_image
      if (
        (!media || media.length === 0) &&
        this.selectedSwatchData?.swatch_image
      ) {
        media = [
          `<img src="${this.selectedSwatchData.swatch_image}" loading="lazy" alt="${this.product.title || ""}">`,
        ];
      }
      if (media && media.length > 0) {
        const firstMedia = media[0].replace('loading="lazy"', "");
        const mediaLinkId = `product-card-media-link-${this.product.handle}`;
        const mediaLinkHref = this.getVariantUrl();
        const mediaLinkClass = `product-card__media-link ${media.length > 1 ? "product-card__media-link--hover" : ""}`;

        // Priority-shell hydrate-in-place: the shell already painted an
        // <a.product-card__media-link><img> (often the LCP element). Reuse
        // those nodes instead of replacing them so the browser never
        // resets/redecodes the visible image.
        const existingMediaLink = mediaContainer.querySelector("a.product-card__media-link");
        const existingImage = existingMediaLink?.querySelector("img");
        const hydratedImage = createElementFromHTML(firstMedia);

        if (existingMediaLink && existingImage && hydratedImage?.tagName === "IMG") {
          existingMediaLink.id = mediaLinkId;
          existingMediaLink.href = mediaLinkHref;
          existingMediaLink.className = mediaLinkClass;
          existingMediaLink.removeAttribute("aria-label");
          // Apply srcset/sizes before src so the browser never fetches the
          // low-width src fallback from the hydrated markup.
          for (const attributeName of ["srcset", "sizes"]) {
            const attributeValue = hydratedImage.getAttribute(attributeName);
            if (attributeValue) existingImage.setAttribute(attributeName, attributeValue);
          }
          for (const { name, value } of [...hydratedImage.attributes]) {
            if (name === "style" || name === "loading") continue;
            existingImage.setAttribute(name, value);
          }
          // Drop the shell's inline sizing so the element matches the
          // stylesheet-driven rendering of a normally hydrated card.
          existingImage.removeAttribute("style");
          // Remove any shell-only siblings, then append the hover image.
          for (const child of [...existingMediaLink.children]) {
            if (child !== existingImage) child.remove();
          }
          if (media.length > 1) {
            const hoverImage = createElementFromHTML(media[1]);
            if (hoverImage && !hoverImage.hasAttribute("alt")) {
              hoverImage.setAttribute("alt", this.product?.title || "");
            }
            existingMediaLink.appendChild(hoverImage);
          }
        } else {
          // clear the media container
          mediaContainer.replaceChildren();
          let mediaContainerHTML = `
            <a id="${mediaLinkId}" href="${mediaLinkHref}" class="${mediaLinkClass}">
              ${firstMedia}
              ${media.length > 1 ? media[1] : ""}
            </a>
          `;
          // insert the link node into the media container
          const mediaLinkElement = createElementFromHTML(mediaContainerHTML);
          for (const img of mediaLinkElement.querySelectorAll("img:not([alt])")) {
            img.setAttribute("alt", this.product?.title || "");
          }
          mediaContainer.appendChild(mediaLinkElement);
        }
      }
    }
    /**
     * Renders badges in a specific container with location filtering
     * @param {HTMLElement} container - The container element to render into
     * @param {string} targetLocation - The target location ("Image label" or "Card info")
     * @param {string} containerClass - CSS class for the badge container
     */
    renderBadges(container, targetLocation, containerClass) {
      const badgeHTML = this.getFilteredBadgesHTML(targetLocation);
      if (badgeHTML.length > 0) {
        const badgeContainerHTML = `<div class="${containerClass}">${badgeHTML}</div>`;
        const badgeElement = createElementFromHTML(badgeContainerHTML);
        // Insert as first child to ensure badges appear at the top
        // If no children exist, appendChild will be used automatically
        if (container.firstChild) {
          container.insertBefore(badgeElement, container.firstChild);
        } else {
          container.appendChild(badgeElement);
        }
      }
    }

    /**
     * Renders the product info badges portion of the card
     * @param {HTMLElement} container - The container element to render into
     */
    renderClientSideCardInfoBadges(container) {
      const infoBadgesContainer =
        this.clearSlot(container, "[data-slot='info-badges']") ||
        container.querySelectorAll(".product-card__info")[0];
      if (!infoBadgesContainer) return;
      this.renderBadges(infoBadgesContainer, "Card info", "product-card__badges");
    }

    /**
     * Renders the product title portion of the card
     * @param {HTMLElement} container - The container element to render into
     */
    renderClientSideCardTitle(container) {
      // get the info container
      const cardTitleContainer =
        this.clearSlot(container, "[data-slot='title']") ||
        container.querySelector(".product-card__info");
      const subtitleContainer = this.clearSlot(container, "[data-slot='subtitle']");
      // early exit conditions
      if (!cardTitleContainer) return;

      const titleHTML = `
        <h3 class="product-card__title">
          <a id="product-title-link-${this.product.handle}" href="${this.getVariantUrl()}" class="product-card__title-link">
            ${this.product.title_html || this.product.title || "Product"}
          </a>
        </h3>
      `;
      // insert the title into the info container
      cardTitleContainer.appendChild(createElementFromHTML(titleHTML));
      // product subtitle, if applicable
      const productSubtitle = this.product.subtitle;
      if (productSubtitle) {
        const subtitleHTML = `
          <p class="product-card__subtitle">${productSubtitle}</p>
        `;
        (subtitleContainer || cardTitleContainer).appendChild(
          createElementFromHTML(subtitleHTML),
        );
      }
    }

    /**
     * Renders the price portion of the product card
     * @param {HTMLElement} container - The container element to render into
     */
    renderClientSideCardPrice(container) {
      // get the info container, price element is a child of the info container
      const priceContainer =
        this.clearSlot(container, "[data-slot='price']") ||
        container.querySelector(".product-card__info");
      // early exit conditions
      if (!priceContainer) return;
      const priceInnerHTML = `<div class="product-card__price-option">${this.variant.price_html}</div><div class="product-card__price-range">${this.product.price_range_html}</div>`;
      const priceHTML = this.showSoldOutState
        ? `<span class="price--sold-out">${window.theme.language_strings.variant_sold_out}</span>`
        : priceInnerHTML;
      const priceContainerHTML = `
        <div class="product-card__price">
          ${priceHTML}
        </div>
      `;
      priceContainer.appendChild(createElementFromHTML(priceContainerHTML));
    }

    /**
     * Renders the color swatches portion of the product card
     * @param {HTMLElement} container - The container element to render into
     */
    renderClientSideCardSwatches(container) {
      // get the swatches container
      const cardContent =
        this.clearSlot(container, "[data-slot='swatches']") ||
        container.querySelector(".product-card__content");
      // early exit conditions
      if (
        !cardContent ||
        !this.product.swatches ||
        !this.product.swatches.length ||
        !this.product.available_colors.length
      )
        return;

      // maximum number of swatches to display
      const maxSwatches = 3;
      const prioritizeSaleVariants = this.shouldPrioritizeSaleVariants();
      const prioritizedSaleSwatches = prioritizeSaleVariants
        ? this.findPrioritizedSaleSwatches(maxSwatches)
        : [];
      const availableColorSet = this.getNormalizedColorSet(
        this.product.available_colors || [],
      );

      // Find current variant's swatch using option value ID first.
      const currentVariantColor = handleize(String(this.variant.color || ""));
      const currentVariantSwatch = this.getSelectedVariantSwatch();

      // Create a prioritized list of swatches
      // 1. Start with the current variant's swatch
      // 2. Add other available swatches until we reach maxSwatches
      let swatchesToRender = [];
      const seenSwatchKeys = new Set();

      const addSwatchIfEligible = (swatch) => {
        if (!swatch || swatchesToRender.length >= maxSwatches) return;
        if (swatch.available === false) return;
        const normalizedSwatchColor = handleize(
          String(swatch.color || swatch.name || ""),
        );
        if (
          availableColorSet.size > 0 &&
          !availableColorSet.has(normalizedSwatchColor)
        )
          return;

        const swatchKey = String(
          swatch.option_value_id || swatch.handle || swatch.color,
        );
        if (seenSwatchKeys.has(swatchKey)) return;

        seenSwatchKeys.add(swatchKey);
        swatchesToRender.push(swatch);
      };

      if (prioritizeSaleVariants && prioritizedSaleSwatches.length > 0) {
        prioritizedSaleSwatches.forEach((swatch) => addSwatchIfEligible(swatch));
      }

      // Add current variant swatch first (if not already included)
      if (currentVariantSwatch) addSwatchIfEligible(currentVariantSwatch);

      // Add other available swatches until we reach maxSwatches
      if (this.product.swatches.length > 1) {
        this.product.swatches.forEach((swatch) => {
          addSwatchIfEligible(swatch);
        });
      }

      // Only continue if we have swatches to render
      if (swatchesToRender.length === 0) return;

      // start the swatches container html
      const colorOptionMeta = this.getColorOptionMeta();
      const colorOptionName = colorOptionMeta.name || "Color";
      const colorOptionKey = colorOptionMeta.key || "option1";
      let swatchesContainerHTML = `
        <div class="product-card-swatches__container">
          <ul
            class="product-card-swatches product-card__option product-card__options--${handleize(colorOptionName)}"
            data-option="${colorOptionName}"
            data-option-key="${colorOptionKey}"
          >
      `;

      // Render each swatch in our prioritized order
      const selectedSwatchOptionValueId = String(
        currentVariantSwatch?.option_value_id || "",
      ).trim();
      const selectedSwatchHandle = String(
        currentVariantSwatch?.handle || "",
      ).trim();

      swatchesToRender.forEach((swatch) => {
        const swatchOptionValueId = String(
          swatch.option_value_id || "",
        ).trim();
        const swatchHandle = String(swatch.handle || "").trim();
        const isSelectedSwatch = selectedSwatchOptionValueId
          ? swatchOptionValueId === selectedSwatchOptionValueId
          : selectedSwatchHandle && swatchHandle === selectedSwatchHandle;
        swatchesContainerHTML += `
          <li style="--swatch-image: url('${swatch.swatch_image}');--swatch-image-aspect-ratio: ${swatch.swatch_image_aspect_ratio};">
            <div
              class="product-card-swatch swatch--${swatch.handle}"
            >
              <input
                type="radio"
                id="product-${this.product.handle}-color-${swatch.handle}"
                name="color[${this.product.handle}]"
                value="${swatch.color}"
                class="product__option-input product__option-swatch"
                data-color="${swatch.handle}"
                data-option-key="${colorOptionKey}"
                data-option-value-id="${swatch.option_value_id || ""}"
                ${isSelectedSwatch ? "checked" : ""}
              >
              <label
                for="product-${this.product.handle}-color-${swatch.handle}"
                class="product__option-label"
              >
                <span class="visually-hidden">${swatch.color}</span>
              </label>
            </div>
          </li>
        `;
      });

      const availableSwatchCount = this.product.swatches.reduce(
        (count, swatch) => {
          if (!swatch || swatch.available === false) return count;
          const normalizedSwatchColor = handleize(
            String(swatch.color || swatch.name || ""),
          );
          if (
            availableColorSet.size > 0 &&
            !availableColorSet.has(normalizedSwatchColor)
          ) {
            return count;
          }
          return count + 1;
        },
        0,
      );

      // Add "more colors" link if we have more swatches than the max
      if (availableSwatchCount > maxSwatches) {
        swatchesContainerHTML += `
          <li class="product-card-swatches-link-more">
            <a
              id="product-more-colors-link-${this.product.handle}"
              href="${this.getVariantUrl()}"
              class="link product-card__more-colors-link"
            >
              ${window.theme.language_strings.product_swatches_more_colors}
            </a>
          </li>
        `;
      }

      // close the swatches container
      swatchesContainerHTML += `
          </ul>
        </div>
      `;

      // insert the swatches container into the container
      cardContent.appendChild(createElementFromHTML(swatchesContainerHTML));

      // Log information if debug is enabled
      if (this.allowDebug()) {
        console.log(
          "product-card.js: renderClientSideCardSwatches(), Rendered swatches:",
          swatchesToRender.map((s) => s.color),
          this,
        );
        console.log(
          "product-card.js: renderClientSideCardSwatches(), Current variant color:",
          currentVariantColor,
          this,
        );
      }
    }

    /**
     * Sets up event listeners for the product card
     * Uses event delegation for efficiency
     */
    setEventListeners() {
      // event delegation via clicks

      this.addEventListener("click", (event) => {
        // swatch click
        const { target } = event;
        // find the grid item
        const gridItem = target.closest("li.product-grid__item") || target.closest("li.grid__item");
        // remove the nudge class if present
        if (gridItem?.classList.contains("nudge"))
          gridItem.classList.remove("nudge");
        // handle swatch click
        if (this.hasSwatches()) {
          const swatchInput = this.getSwatchInputFromEventTarget(target);
          if (swatchInput) this.handleProductSwatchClick(swatchInput);
        }
        // Track product link clicks for Nosto analytics
        // See: https://docs.nosto.com/techdocs/implementing-nosto/implement-search/search#analytics
        const productLink = target.closest('a[href*="/products/"]');
        // Use dataset.productId as fallback for client-side rendered cards where this.product may not be loaded yet
        const hasProductId = this.product?.id || this.dataset.productId;
        if (productLink && this.dataset.vendor === "nosto" && hasProductId) {
          this.handleNostoProductClick();
        }
      });
      // handle swatch change
      this.addEventListener(
        "product-card:swatch-change",
        this.handleSwatchChange,
      );
      // NOTE: quick-add variant updates intentionally use local bubbling CustomEvents
      // within the card tree instead of global pub-sub, because this interaction is
      // isolated to a single product-card instance and does not need cross-page channels.
      this.addEventListener(
        "quick-add-to-bag:variant-change",
        this.boundHandleQuickAddVariantChange,
      );
      // hover/focus a swatch -> prefetch its variant + media (once)
      this.addEventListener("mouseover", (event) =>
        this.prefetchSwatchFromEventTarget(event.target),
      );
      this.addEventListener("focusin", (event) =>
        this.prefetchSwatchFromEventTarget(event.target),
      );
      // handle promotion change via pub-sub
      this.unsubscribers.push(
        subscribe(
          EVENTS.PRODUCT_PROMOTION_CHANGE,
          this.boundHandlePromotionChange,
        ),
      );
      // handle mouseover on card_inner
      const cardInner = this.querySelector(".product-card__inner");
      if (cardInner) {
        cardInner.addEventListener(
          "mouseenter",
          this.handleCardMediaHoverEnter.bind(this),
        );
      }
      this.setMediaInteractionListeners();
      this.currentMediaMode = this.getMediaMode();
      window.addEventListener("resize", this.boundHandleViewportModeChange, {
        passive: true,
      });
    }

    /**
     * Prefetch the variant + media for the swatch under an event target, once.
     * Shared by the mouseover and focusin listeners.
     * @param {EventTarget} target
     */
    prefetchSwatchFromEventTarget(target) {
      const swatchInput = this.getSwatchInputFromEventTarget(target);
      if (!swatchInput || swatchInput.dataset.prefetchRequested === "true")
        return;

      const optionValueId = swatchInput.dataset.optionValueId;
      if (!optionValueId) return;

      swatchInput.dataset.prefetchRequested = "true";
      const matchingSwatch = this.product?.swatches?.find(
        (swatch) => String(swatch.option_value_id) === String(optionValueId),
      );
      const prefetchBaseUrl = matchingSwatch?.url || this.getActiveProductUrl();
      this.prefetchVariantData([String(optionValueId)], prefetchBaseUrl);
      this.prefetchSwatchMedia(matchingSwatch);
    }

    getSwatchInputFromEventTarget(target) {
      if (!target) return null;
      const directInput = target.closest(
        "input.product__option-input.product__option-swatch",
      );
      if (directInput) return directInput;

      const swatchContainer = target.closest(".product-card-swatch");
      if (!swatchContainer) return null;

      return swatchContainer.querySelector(
        "input.product__option-input.product__option-swatch",
      );
    }

    setMediaInteractionListeners() {
      const cardMedia = this.querySelector(".product-card__media");
      if (!cardMedia) return;

      const dragThreshold = 8;
      const suppressClickDurationMs = 350;
      const markDrag = () => {
        this.mediaPointerState.moved = true;
        this.mediaPointerState.suppressClickUntil =
          Date.now() + suppressClickDurationMs;
      };
      const resetPointer = () => {
        this.mediaPointerState.isPointerDown = false;
        this.mediaPointerState.startX = 0;
        this.mediaPointerState.startY = 0;
      };

      cardMedia.addEventListener(
        "pointerdown",
        (event) => {
          if (event.button !== undefined && event.button !== 0) return;
          // Touch/swipe intent on devices without hover — build the lazy gallery
          // slider now so the first swipe has slides to move through.
          this.ensureSwatchMediaSlider();
          this.mediaPointerState.isPointerDown = true;
          this.mediaPointerState.moved = false;
          this.mediaPointerState.startX = event.clientX || 0;
          this.mediaPointerState.startY = event.clientY || 0;
        },
        { passive: true },
      );

      cardMedia.addEventListener(
        "pointermove",
        (event) => {
          if (!this.mediaPointerState.isPointerDown || this.mediaPointerState.moved)
            return;
          const deltaX = Math.abs((event.clientX || 0) - this.mediaPointerState.startX);
          const deltaY = Math.abs((event.clientY || 0) - this.mediaPointerState.startY);
          if (deltaX > dragThreshold || deltaY > dragThreshold) {
            markDrag();
          }
        },
        { passive: true },
      );

      cardMedia.addEventListener(
        "pointerup",
        () => {
          if (this.mediaPointerState.moved) {
            this.mediaPointerState.suppressClickUntil =
              Date.now() + suppressClickDurationMs;
          }
          resetPointer();
        },
        { passive: true },
      );

      cardMedia.addEventListener(
        "pointercancel",
        () => {
          markDrag();
          resetPointer();
        },
        { passive: true },
      );

      cardMedia.addEventListener(
        "dragstart",
        () => {
          markDrag();
          resetPointer();
        },
        { passive: true },
      );

      cardMedia.addEventListener(
        "click",
        (event) => {
          const slideLink = event.target?.closest(".plp-card-slide-link");
          if (!slideLink) return;
          if (Date.now() < this.mediaPointerState.suppressClickUntil) {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        { capture: true, passive: false },
      );
    }

    /**
     * Sets up mutation observers to watch for changes to the component
     * Particularly watches for inline shopping changes that affect quick shop functionality
     */
    setObservers() {
      // Track the initial class state for comparison.
      let previousClassList = [...this.classList];
      let previousInlineShopping = this.dataset.inlineShopping || "none";
      // class change observer
      const attributeChangeObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type !== "attributes") return;

          if (mutation.attributeName === "class") {
            const currentClassList = [...this.classList];
            const addedClasses = currentClassList.filter(
              (cls) => !previousClassList.includes(cls),
            );
            if (addedClasses.includes("debug")) {
              if (this.allowDebug())
                console.log(
                  "product-card.js: attributeChangeObserver(), debug class has been added for the card.",
                  this,
                );
            }
            previousClassList = currentClassList;
          }

          if (mutation.attributeName === "data-inline-shopping") {
            const currentInlineShopping = this.dataset.inlineShopping || "none";
            if (currentInlineShopping !== previousInlineShopping) {
              if (this.allowDebug())
                console.log(
                  "product-card.js: attributeChangeObserver(), inline shopping mode changed.",
                  this,
                );
              this.injectQuickShop();
              previousInlineShopping = currentInlineShopping;
            }
          }
        });
      });

      attributeChangeObserver.observe(this, {
        attributes: true,
        attributeFilter: ["class", "data-inline-shopping"],
      });
    }

    /**
     * Injects the appropriate quick shop interface based on inline shopping mode
     * Either quick view or quick add to bag functionality
     */
    injectQuickShop() {
      // early exit conditions
      if (this.product.is_bundle_product) return;
      // quick shop container is a sibling of .product-card__media
      const cardMedia = this.querySelector(".product-card__media");
      if (!cardMedia) return;

      const inlineShopping = this.dataset.inlineShopping || "none";
      const hasQuickView = inlineShopping === "modal";
      const hasAddToBag = inlineShopping === "add-to-bag";

      // Just in case, clear the quickshop container.
      const quickShopContainer = this.querySelector(".product-card__quick-shop");
      if (quickShopContainer) {
        quickShopContainer.remove();
      }

      // Inject quickshop action code on the present classes.
      try {
        // inject the quick view
        if (hasQuickView) {
          const content = createElementFromHTML(
            this.templates.quickViewTemplateHTML,
          );
          // insert the new quick shop container after .product-card__media
          cardMedia.after(content);
        } else if (hasAddToBag) {
          // inject the quick add to bag
          const content = createElementFromHTML(
            this.templates.quickAddToBagTemplateHTML,
          );
          // insert the new quick shop container after .product-card__media
          cardMedia.after(content);
        }
      } catch (error) {
        console.error("Error injecting quick shop content:", error, this);
      }
    }

    /**
     * Renders the rewards messaging text in the appropriate location
     * @param {HTMLElement} container - The container element to render into
     */
    renderCardRewardsText(container) {
      const cardMedia = container.querySelector(".product-card__media");
      const quickAddToBagButton = container.querySelector(".quick-add-to-bag");
      if (!cardMedia && !quickAddToBagButton) {
        return;
      }

      // Render rewards messaging if applicable
      const showRewardsText = this.dataset.showRewardsText;
      if (stringToBoolean(showRewardsText)) {
        const rewardsTextTemplateHTML = `
          <div class="product-card__rewards">
            <p class="product-card__rewards-text normal">
              ${window.theme.language_strings.membership_rewards}
            </p>
          </div>
        `;

        if (quickAddToBagButton) {
          // If quick add to bag button exists, prepend the messaging inside the button
          quickAddToBagButton.prepend(
            createElementFromHTML(rewardsTextTemplateHTML),
          );
        } else {
          // Otherwise, insert the messaging after the media container
          cardMedia.after(createElementFromHTML(rewardsTextTemplateHTML));
        }
      }
    }

    /**
     * Sets default swatch data when there's only one swatch option
     * Simulates a user click on the first swatch
     */
    setDefaultSwatchData() {
      if (this.allowDebug())
        console.log("product-card.js: setDefaultSwatchData()", this);
      // if we do not have a selected swatch, get the first swatch
      let selectedSwatch = this.querySelector(
        ".product__option-swatch:checked",
      );
      if (!selectedSwatch) {
        selectedSwatch = this.elements.productSwatches?.querySelector(
          ".product__option-swatch:first-of-type",
        );
      }
      // set the selected swatch data, if the element is null, the function sets empty values.
      this.setSelectedSwatchData(selectedSwatch);
    }

    /**
     * Handles promotion change events from the promotion component
     * @param {CustomEvent} event - The promotion change event
     */
    handlePromotionChange(data) {
      // early exit conditions
      if (this.contains(data.triggeringElement)) {
        this.promotion = data.promotion;
        // An empty promotion means the discount no longer applies (e.g. the shopper
        // picked a swatch the promotion excludes). Restore the undiscounted server
        // price rather than leaving a stale cross-out on screen.
        if (!getPromotionDiscount(this.promotion)) {
          this.restoreServerPrice();
          return;
        }
        // The promotion is lazy-fetched, so the price is already rendered by now.
        this.applyPromotionToPrice();
      }
    }

    /**
     * Re-renders the last server-provided price markup, dropping any applied discount.
     *
     * Used when a promotion stops applying. The server HTML is the source of truth for
     * the variant's real price, so re-rendering it is what clears the discount.
     */
    restoreServerPrice() {
      if (!this.lastPriceHtml) return;
      if (!this.elements.priceElement) return;
      this.elements.priceElement.replaceChildren(
        renderPriceMarkup(this.lastPriceHtml, null),
      );
      this.promotionApplied = false;
    }

    /**
     * Applies the active promotion discount to the rendered card price.
     *
     * Runs after every price render. setPriceElement replaces the price with fresh
     * server HTML on each swatch and variant change, and that markup carries no
     * discount, so the discount must be re-applied each time.
     */
    applyPromotionToPrice() {
      this.promotionApplied = false;
      if (!getPromotionDiscount(this.promotion)) return;
      if (!this.elements.priceElement) return;
      if (!this.lastPriceHtml) return;
      // Rebuild from the stored server markup in one mutation, so the price never
      // paints undiscounted and then changes.
      this.elements.priceElement.replaceChildren(
        renderPriceMarkup(this.lastPriceHtml, this.promotion),
      );
      this.promotionApplied = !!this.elements.priceElement.querySelector(
        '.price-item[data-promotion-applied="true"]',
      );
    }

    /**
     * Updates promotion variables when the variant changes
     * @param {Object} variant - The product variant object
     */
    async updatePromotionVariables(variant) {
      if (
        !variant ||
        !this.elements.productPromotion ||
        this.elements.productPromotion.lazyLoad
      )
        return;

      const promotionFetchData = {
        url: variant.url || this.product?.url || this.dataset.productUrl || "#",
      };

      await this.elements.productPromotion.updateFetchData(promotionFetchData);
    }

    /**
     * Handles product swatch click events
     * Updates the selected swatch data and dispatches a swatch change event
     * @param {HTMLElement} swatch - The swatch input element that was clicked
     */
    handleProductSwatchClick(swatchElement) {
      // early exit conditions
      if (!swatchElement) return;
      // set the selected swatch dataset
      this.setSelectedSwatchData(swatchElement);
      // dispatch swatch change event
      this.dispatchEvent(
        new CustomEvent("product-card:swatch-change", {
          bubbles: true,
          detail: {
            triggeringElement: this,
            swatchData: this.selectedSwatchData,
            swatchElement: this.selectedSwatchElement,
          },
        }),
      );
    }

    /**
     * Sets the selected swatch data and element
     * @param {Object} swatch - The swatch data object
     */
    setSelectedSwatchData(swatchElement) {
      // Find the matching swatch object by option value ID first, then by color handle.
      const optionValueId = swatchElement?.dataset?.optionValueId;
      const handleizedColor = swatchElement?.dataset?.color || "";
      let matchingSwatch = this.product?.swatches?.find((swatch) => {
        if (optionValueId) {
          return String(swatch.option_value_id) === String(optionValueId);
        }
        return swatch.handle === handleizedColor;
      });
      // exit if no matching swatch found
      if (!matchingSwatch) {
        if (this.allowDebug())
          console.log(
            "product-card.js: setSelectedSwatchData(), no matching swatch found",
            swatchElement,
          );
        // reset the swatch data
        matchingSwatch = {};
        swatchElement = null;
      }
      // set the selected swatch dataset
      this.selectedSwatchData = matchingSwatch;
      // set the selected swatch element
      this.selectedSwatchElement = swatchElement;
    }

    normalizeImageAssetPath(imageUrl = "") {
      const rawImageUrl = String(imageUrl || "").trim();
      if (!rawImageUrl) return "";

      try {
        const parsedUrl = new URL(rawImageUrl, window.location.origin);
        const fileMarkerIndex = parsedUrl.pathname.indexOf("/files/");
        const normalizedPath =
          fileMarkerIndex !== -1
            ? parsedUrl.pathname.slice(fileMarkerIndex)
            : parsedUrl.pathname;
        return normalizedPath.toLowerCase();
      } catch (_error) {
        return rawImageUrl.split("?")[0].split("#")[0].toLowerCase();
      }
    }

    extractImageSrcFromMarkup(imageMarkup = "") {
      const markup = String(imageMarkup || "").trim();
      if (!markup) return "";

      const parsedDocument = cardMediaDomParser.parseFromString(
        markup,
        "text/html",
      );
      const image = parsedDocument.querySelector("img");
      return image?.getAttribute("src") || "";
    }

    /**
     * Lazily build the gallery slider for the preserved (painted) media on the
     * first real user interaction — hover/focus of the media, or a swatch
     * change. Until called, the card shows only the static painted image (no
     * flash on settle). Idempotent and a no-op when the slider already exists,
     * the gallery is disabled, or there is only a single image. Runs the normal
     * setProductImagesFromSwatch rebuild (without preserve) so the slider is
     * created exactly as a non-preview card's would be.
     */
    ensureSwatchMediaSlider() {
      if (!this.deferredMediaSliderPending) return;
      this.deferredMediaSliderPending = false;
      if (this.disableGallery) return;
      if (this.slider) return;
      if (!this.selectedSwatchData) return;
      this.setProductImagesFromSwatch(this.selectedSwatchData);
    }

    /**
     * Whether the grid-injected media should be left untouched during hydrate.
     * True only for payload-rendered (preview) cards whose painted image still
     * matches the selected swatch's asset — so we never repaint the settled
     * image. A swatch change (different asset) returns false and falls through to
     * the normal media render/rebuild.
     *
     * @param {HTMLElement} mediaContainer - The .product-card__media element
     * @returns {boolean}
     */
    shouldPreserveInjectedMedia(mediaContainer) {
      if (!this.cardPayload) return false;
      if (!mediaContainer) return false;
      const swatch = this.getSelectedVariantSwatch() || this.selectedSwatchData;
      if (!swatch) return false;
      const swatchImages =
        (Array.isArray(swatch.media) && swatch.media.length > 0
          ? swatch.media
          : this.product?.media) || [];
      return this.canPreserveInitialMediaForSwatch(
        mediaContainer,
        swatch,
        swatchImages,
      );
    }

    canPreserveInitialMediaForSwatch(cardMediaContainer, swatch, swatchImages) {
      if (!cardMediaContainer) return false;

      // Mobile has no hover state to opportunistically build the gallery on
      // later — the only trigger is the user's first touch, which is often
      // the tap that navigates to the PDP. Build the real gallery structure
      // now instead of deferring, so it's ready (dots included) as soon as
      // the card is visible, and so a swipe never lands on an intermediate
      // shape that has to be swapped out mid-interaction.
      if (isMobile() && Array.isArray(swatchImages) && swatchImages.length > 1) {
        return false;
      }

      const existingImage = cardMediaContainer.querySelector("img");
      if (!existingImage) return false;

      const existingSrc =
        existingImage.currentSrc || existingImage.getAttribute("src") || "";
      if (!existingSrc) return false;

      const firstSwatchImageMarkup =
        Array.isArray(swatchImages) && swatchImages.length > 0
          ? swatchImages[0]
          : "";
      const targetSrc =
        this.extractImageSrcFromMarkup(firstSwatchImageMarkup) ||
        swatch?.swatch_image ||
        "";

      // If we don't have a target to compare, keep the already-rendered image.
      if (!targetSrc) return true;

      return (
        this.normalizeImageAssetPath(existingSrc) ===
        this.normalizeImageAssetPath(targetSrc)
      );
    }

    /**
     * Updates the product images based on the selected swatch
     * Creates a slider if there are multiple images and it's not disabled
     * @param {Object} swatch - The swatch data object
     */
    setProductImagesFromSwatch(
      swatch,
      { preserveInitialMedia = false } = {},
    ) {
      if (this.allowDebug())
        console.log(
          "product-card.js: setProductImagesFromSwatch(), setting product images from swatch",
          swatch,
        );
      // early exit conditions
      if (!swatch) return;
      // get the media wrapper
      const cardMediaContainer = this.querySelector(".product-card__media");
      if (!cardMediaContainer) return;
      // get the swatch images
      // swatches images is an array of HTML image tag strings.
      const swatchImages =
        Array.isArray(swatch.media) && swatch.media.length > 0
          ? swatch.media
          : Array.isArray(this.product.media)
            ? this.product.media
            : [];
      if (
        preserveInitialMedia &&
        this.canPreserveInitialMediaForSwatch(
          cardMediaContainer,
          swatch,
          swatchImages,
        )
      ) {
        // Leave the painted media node COMPLETELY untouched on hydrate. Any DOM
        // work here (rebuilding, or even layering a slider sibling) caused a
        // visible repaint/"flash" of the same image and risked disturbing the
        // LCP element. The gallery slider is instead built lazily on the first
        // real user interaction (media hover/focus, or a swatch change) — see
        // ensureSwatchMediaSlider(). Until then the static image is all we show.
        this.deferredMediaSliderPending = swatchImages.length > 1;
        this.currentMediaMode = this.getMediaMode();
        return;
      }

      // This rebuild path produces the slider itself, so any pending lazy build
      // is satisfied — clear the flag so a later hover doesn't re-trigger it.
      this.deferredMediaSliderPending = false;
      // if we have a slider, destroy it.
      this.destroySwatchMediaSlider();
      // clear the main media wrapper
      cardMediaContainer.replaceChildren();
      const swatchUrl = this.resolveCardNavigationUrl(
        swatch.url || this.product?.url || "#",
      );
      const staticMediaImageHTML =
        swatchImages[0] ||
        this.product.media?.[0] ||
        (swatch.swatch_image
          ? `<img src="${swatch.swatch_image}" loading="lazy" alt="${this.product.title || ""}">`
          : "");
      if (this.disableGallery) {
        if (staticMediaImageHTML) {
          const staticMediaLink = createElementFromHTML(`
            <a
              href="${swatchUrl}"
              id="CardLink-${this.dataset.sectionId}-${this.product.id}"
              class="product-card__media-link"
              aria-labelledby="CardLink-${this.dataset.sectionId}-${this.product.id} Badge-${this.dataset.sectionId}-${this.product.id}"
            >
              ${staticMediaImageHTML}
            </a>
          `);
          cardMediaContainer.appendChild(staticMediaLink);
        }
        return;
      }

      const mediaMode = this.getMediaMode();
      const shouldUseLayeredDesktopMedia =
        mediaMode === "desktop-layered" && swatchImages.length > 1;
      this.currentMediaMode = mediaMode;

      if (shouldUseLayeredDesktopMedia) {
        const primaryImageHTML = swatchImages[0];
        if (primaryImageHTML) {
          const primaryMediaLink = createElementFromHTML(`
            <a
              href="${swatchUrl}"
              id="CardLink-${this.dataset.sectionId}-${this.product.id}"
              class="product-card__media-link plp-card-primary-media"
              aria-labelledby="CardLink-${this.dataset.sectionId}-${this.product.id} Badge-${this.dataset.sectionId}-${this.product.id}"
            >
              ${primaryImageHTML}
            </a>
          `);
          cardMediaContainer.appendChild(primaryMediaLink);
        }

        this.generateSwatchMediaSlider(swatch, {
          swatchImages,
          reorderPrimaryImageToEnd: true,
          skipInitialFade: preserveInitialMedia,
        });
      } else if (swatchImages.length > 0) {
        this.generateSwatchMediaSlider(swatch, {
          swatchImages,
          reorderPrimaryImageToEnd: false,
          skipInitialFade: preserveInitialMedia,
        });
      } else {
        // Fallback to default product media if no swatch images
        // add the first image as static media fallback
        const defaultImageHTML = staticMediaImageHTML;
        if (defaultImageHTML) {
          const fallbackMediaLink = createElementFromHTML(`
            <a
              href="${swatchUrl}"
              id="CardLink-${this.dataset.sectionId}-${this.product.id}"
              class="product-card__media-link"
              aria-labelledby="CardLink-${this.dataset.sectionId}-${this.product.id} Badge-${this.dataset.sectionId}-${this.product.id}"
            >
              ${defaultImageHTML}
            </a>
          `);
          cardMediaContainer.appendChild(fallbackMediaLink);
        }
      }
    }

    generateSwatchMediaSlider(
      swatch = null,
      {
        swatchImages: providedSwatchImages = null,
        reorderPrimaryImageToEnd = false,
        skipInitialFade = false,
      } = {},
    ) {
      if (this.allowDebug())
        console.log("product-card.js: generateSwatchMediaSlider()", this);
      if (this.disableGallery) return;
      if (!swatch) return;
      // get the card media container
      const cardMediaContainer = this.querySelector(".product-card__media");
      if (!cardMediaContainer) return;
      // resolve the navigation URL (color-only partial hydration when enabled)
      const swatchUrl = this.resolveCardNavigationUrl(
        swatch.url || this.product?.url || "#",
      );
      // get the swatch images
      const swatchImages = Array.isArray(providedSwatchImages)
        ? providedSwatchImages
        : Array.isArray(swatch.media) && swatch.media.length > 0
          ? swatch.media
          : Array.isArray(this.product.media)
            ? this.product.media
            : [];
      if (!swatchImages) return;
      const initialIndex = 0;
      const baseImages = swatchImages.slice(0, 5);
      const sliderImages =
        reorderPrimaryImageToEnd && baseImages.length > 1
          ? [...baseImages.slice(1), baseImages[0]]
          : baseImages;
      const sliderId = `${this.id || `product-card-${this.product?.id || "unknown"}`}__swatch-slider`;
      const carouselClass = skipInitialFade
        ? "plp-card-carousel embla embla--arrows-inside plp-card-carousel--no-fade"
        : "plp-card-carousel embla embla--arrows-inside";
      // Create carousel HTML with template literals
      let sliderHTML = `
        <embla-slider class="${carouselClass}" id="${sliderId}">
          <script type="application/json" id="${sliderId}__json" component-config>
            {
              "options": {
                "loop": true,
                "align": "start",
                "slidesToScroll": 1,
                "initialIndex": ${initialIndex}
              }
            }
          </script>
          <button type="button" class="embla__button embla__button--prev" aria-label="Previous image">
            <svg class="embla__button__svg" viewBox="0 0 100 100"><path d="M 10 50 L 50 90 L 55 85 L 20 50 L 55 15 L 50 10 Z" class="arrow" fill="#333333"></path></svg>
          </button>
          <div class="embla__viewport">
            <div class="embla__container">
      `;
      // Add carousel cells (limited to first 5)
      sliderImages.forEach((imageTag, index) => {
        let imageTagHTML = imageTag;
        if (index === 0 || index === 1) {
          imageTagHTML = imageTag.replace('loading="lazy"', "");
        }
        const isInitiallySelected = index === initialIndex;
        let carouselCellClass = "embla__slide carousel-cell";
        // Mark selected at build time only for the no-fade (initial hydrate)
        // case, so this same-asset slide is visible immediately with no wait
        // on Embla's async select callback. The genuine-rebuild case (swatch
        // change) leaves this to setEmblaSelectedSlideClass so its deliberate
        // fade-in still plays for what is actually a new image.
        if (isInitiallySelected && skipInitialFade) {
          carouselCellClass += " is-selected carousel-cell--no-fade";
        }
        sliderHTML += `
          <div id="product-card-media-slide-${this.product.handle}-${index}" class="${carouselCellClass}">
            <a
              href="${swatchUrl}"
              class="product-card__media-link plp-card-slide-link"
              tabindex="${isInitiallySelected ? "0" : "-1"}"
              aria-hidden="${isInitiallySelected ? "false" : "true"}"
            >
              ${imageTagHTML}
            </a>
          </div>
        `;
      });
      sliderHTML += `
            </div>
          </div>
          <button type="button" class="embla__button embla__button--next" aria-label="Next image">
            <svg class="embla__button__svg" viewBox="0 0 100 100"><path d="M 10 50 L 50 90 L 55 85 L 20 50 L 55 15 L 50 10 Z" class="arrow" transform="translate(100, 100) rotate(180) " fill="#333333"></path></svg>
          </button>
          <div class="embla__dots"></div>
        </embla-slider>
      `;
      // Append the slider to the media wrapper (as a sibling to the initial media container)
      const sliderWrapper = createElementFromHTML(sliderHTML);
      // insert the slider into the card media container
      cardMediaContainer.appendChild(sliderWrapper);
      // if we are the first grid item, add an identifier class to the slider wrapper
      if (this.isFirstGridItem()) {
        // add the first-slider class to the slider wrapper for extra identification
        sliderWrapper.classList.add("first-slider");
      }
      this.initializeEmblaSliderInstance(
        sliderWrapper,
        sliderImages,
        initialIndex,
        swatchImages,
      );
    }

    destroySwatchMediaSlider() {
      if (this.allowDebug())
        console.log("product-card.js: destroySwatchMediaSlider()", this);
      if (this.customDotsObserver) {
        this.customDotsObserver.disconnect();
        this.customDotsObserver = null;
      }
      if (!this.slider) return;
      // get the card media container
      const cardMediaContainer = this.querySelector(".product-card__media");
      if (!cardMediaContainer) return;
      // destroy the slider
      const emblaApi = this.sliderComponent?.emblaApi;
      if (emblaApi && typeof emblaApi.destroy === "function") {
        emblaApi.destroy();
      }
      this.slider = null;
      this.sliderComponent = null;
      // remove the slider wrapper
      const sliderWrapper =
        cardMediaContainer.querySelector(".plp-card-carousel");
      if (sliderWrapper) {
        cardMediaContainer.removeChild(sliderWrapper);
      }
    }

    /**
     * Handles swatch change events
     * Updates images, price, and variant data based on the new swatch
     * @param {CustomEvent} event - The swatch change event
     * @returns {Promise<void>}
     */
    async handleSwatchChange(event) {
      const swatchUrl =
        this.selectedSwatchData?.url || this.product?.url || "#";

      // update media first (rebuilds media/slider anchors)
      this.setProductImagesFromSwatch(this.selectedSwatchData);

      // update card urls last so every anchor (including freshly built media links)
      // reflects the resolved navigation URL (color-only partial hydration when enabled)
      this.setProductLinks(swatchUrl);

      // update price element to the swatch price
      if (!this.showSoldOutState && this.selectedSwatchData?.price_html) {
        // update the price element
        this.setPriceElement(
          createElementFromHTML(this.selectedSwatchData?.price_html),
        );
      }

      // Swatch changes now only update card presentation state (links/media/price).
      // Variant resolution for quick add is owned by quick-add-to-bag.js.
    }

    async handleQuickAddVariantChange(event) {
      const quickAddToBag = event?.detail?.triggeringElement;
      const variantData = event?.detail?.variantData;
      if (!quickAddToBag || !variantData) return;
      if (!this.contains(quickAddToBag)) return;

      const variant = this.applyVariantData(variantData);
      if (!variant) return;

      await this.updatePromotionVariables(variant);
      this.updateVariantUI(variant);
    }

    /**
     * Option value IDs for the card's current variant from product JSON (`options[].selected_id`),
     * ordered by option position. Aligns quick-add's first HTML request with the variant the
     * card already represents so the follow-up resolve step can skip a duplicate template fetch.
     */
    getResolvedOptionValueIdsForQuickAdd() {
      if (!Array.isArray(this.product?.options)) return [];
      const sorted = [...this.product.options].sort(
        (a, b) => (Number(a.position) || 0) - (Number(b.position) || 0),
      );
      const ids = [];
      for (const opt of sorted) {
        const sid = opt.selected_id;
        if (sid != null && sid !== "") {
          ids.push(String(sid));
        }
      }
      return ids;
    }

    /**
     * Updates the UI elements based on the selected variant
     * @param {Object} variant - The selected variant
     */
    updateVariantUI(variant) {
      // early exit conditions
      if (!variant) return;
      // update the product links
      this.setProductLinks(variant.url);
      // update the price element
      if (!this.showSoldOutState) {
        this.setPriceElement(createElementFromHTML(variant.price_html));
      }
    }

    /**
     * Resolve the shared Nosto attribution context from this card's dataset.
     * Maps the Nosto context string to a searchType and refType, and builds the
     * productId + hit object used by both the click and add-to-cart trackers.
     * @returns {{
     *   context: string,
     *   isRecommendationContext: boolean,
     *   searchType: (string|null),
     *   refType: string,
     *   productId: (string|number|undefined),
     *   hit: {productId: string, url: string},
     * }}
     */
    getNostoSearchContext() {
      const context = this.dataset.nostoContext || "product-card";
      const isRecommendationContext = context.includes("recommendation");

      // Map context to Nosto search type
      let searchType = null;
      if (context.includes("collection") || context.includes("category")) {
        searchType = "category";
      } else if (
        context.includes("predictive") ||
        context.includes("autocomplete")
      ) {
        searchType = "autocomplete";
      } else if (context.includes("search") || context.includes("serp")) {
        searchType = "serp";
      }

      // Determine refType for recordAttribution:
      // - "cmp" for category merchandising
      // - "os" for onsite search/predictive search
      // - "rec" for recommendation slots
      const refType = isRecommendationContext
        ? "rec"
        : searchType === "category"
          ? "cmp"
          : "os";

      // Get product ID - use dataset.productId as fallback for client-side
      // rendered cards (e.g., PredictiveSearch) where this.product may not be
      // loaded yet due to async fetchProduct()
      const productId = this.product?.id || this.dataset.productId;

      // Build hit object required by the record* APIs
      // Use dataset.fetchUrl as primary source, falling back to this.product.url
      const hit = {
        productId: String(productId),
        url: this.dataset.fetchUrl?.split("?")[0] || this.product?.url || "",
      };

      return { context, isRecommendationContext, searchType, refType, productId, hit };
    }

    /**
     * Handles product link clicks for Nosto analytics
     * Records search click events when users click on product links from Nosto search/category results
     * Uses dual attribution: recordSearchClick for search-specific tracking AND recordAttribution for generic Nosto events
     * @see https://docs.nosto.com/techdocs/implementing-nosto/implement-search/search#analytics
     * @see https://docs.nosto.com/techdocs/apis/frontend/js-apis/record-attribution
     */
    handleNostoProductClick() {
      // Fire-and-forget - don't block navigation
      try {
        const { context, searchType, refType, productId, hit } =
          this.getNostoSearchContext();
        if (!productId) {
          if (this.allowDebug()) {
            console.warn("Nosto click tracking: No product ID available");
          }
          return;
        }

        // Get variant ID for targetFragment (SKU-level tracking)
        // Use current variant ID, fallback to default variant ID from dataset
        const variantId = this.variant?.id || this.defaultVariantId;

        if (this.allowDebug()) {
          console.log("Nosto click tracking:", {
            searchType,
            refType,
            hit,
            context,
            variantId,
          });
        }

        // Import and call asynchronously without awaiting (fire-and-forget)
        // This ensures we don't block the navigation.
        // For recommendation context, avoid search-specific calls to prevent SERP/category misclassification.
        import("@utilities/nosto")
          .then(({ nostoRecordSearchClick, nostoRecordAttribution }) => {
            // 1. Search-specific tracking (search/category/autocomplete contexts only)
            if (searchType) {
              nostoRecordSearchClick(searchType, hit, {
                debug: this.allowDebug(),
              });
            }

            // 2. Generic attribution
            const attributionParams = {
              type: "vp", // View Product
              target: String(productId),
              refType: refType,
              debug: this.allowDebug(),
            };
            if (variantId) {
              attributionParams.targetFragment = String(variantId);
            }
            nostoRecordAttribution(attributionParams);
          })
          .catch((error) => {
            if (this.allowDebug()) {
              console.error("Nosto search click tracking error:", error);
            }
          });
      } catch (error) {
        // Don't block navigation on tracking errors
        if (this.allowDebug()) {
          console.error("Nosto search click tracking error:", error);
        }
      }
    }

    /**
     * Adds the current product variant to the cart
     * Handles different product types (standard, parent, bundle, etc.)
     * @returns {Promise<void>}
     */

    async handleAddToCart() {
      // early exit conditions
      if (!isVariantFullyAvailable(this.variant)) return;

      try {
        // get the atc buttons
        const atcButtons = this.querySelectorAll(".product-card__add-to-bag");

        // set loading state
        this.setAddToCartLoadingState(atcButtons, true);

        // compile the item data being sent to cart
        let properties = this.collectLineItemProperties();

        const item = {
          id: this.variant.id,
          quantity: 1,
          properties: properties,
        };

        // Use standardized cart API
        const { addToCartViaStandardAction } = await import("@utilities/theme");
        await addToCartViaStandardAction(item, { openDrawer: true });

        // Record Nosto search add-to-cart if product came from Nosto search/category data
        // Uses dual attribution: recordSearchAddToCart for search-specific tracking AND recordAttribution for generic Nosto events
        // See: https://docs.nosto.com/techdocs/implementing-nosto/implement-search/search#analytics
        // See: https://docs.nosto.com/techdocs/apis/frontend/js-apis/record-attribution
        if (this.dataset.vendor === "nosto") {
          try {
            const { nostoRecordSearchAddToCart, nostoRecordAttribution } =
              await import("@utilities/nosto");
            const { context, searchType, refType, productId, hit } =
              this.getNostoSearchContext();
            if (!productId) {
              if (this.allowDebug()) {
                console.warn("Nosto ATC tracking: No product ID available");
              }
            } else {
              if (this.allowDebug()) {
                console.log("Nosto ATC tracking:", {
                  searchType,
                  refType,
                  hit,
                  context,
                });
              }

              // Search-specific tracking (search/category/autocomplete contexts only)
              if (searchType) {
                await nostoRecordSearchAddToCart(searchType, hit, {
                  debug: this.allowDebug(),
                });
              }

              // 2. Generic attribution to couple search events to Nosto events for accurate dashboard reporting
              await nostoRecordAttribution({
                type: "cp", // Add to Cart (Cart Product)
                target: String(productId),
                refType: refType,
                debug: this.allowDebug(),
              });
            }
          } catch (error) {
            // Attribution failures should not affect cart operation
            if (this.allowDebug()) {
              console.error("Nosto search add-to-cart tracking error:", error);
            }
          }
        } else {
          if (this.allowDebug()) {
            console.log(
              'ProductCard ATC - Skipping Nosto tracking (vendor is not "nosto")',
            );
          }
        }

        // reset loading state
        this.setAddToCartLoadingState(atcButtons, false);

        // close the active quick add to bag and clear selections
        const activeQuickAddToBag = this.querySelector(
          "quick-add-to-bag.active",
        );
        if (activeQuickAddToBag) {
          activeQuickAddToBag.close();
          activeQuickAddToBag.clearSelections();
        }
      } catch (error) {
        console.error("Product Card: Error adding to cart:", error);

        // reset loading state on error
        const atcButtons = this.querySelectorAll(".product-card__add-to-bag");
        this.setAddToCartLoadingState(atcButtons, false);

        // show error message
        const activeQuickAddToBag = this.querySelector(
          "quick-add-to-bag.active",
        );
        if (activeQuickAddToBag) {
          activeQuickAddToBag.setCartMessage(
            error.message || "Error adding to cart",
          );
        }
      }
    }

    /**
     * Collect line item properties from promotion and hidden inputs
     * @returns {Object} Properties object
     */
    collectLineItemProperties() {
      let properties = {};

      // Collect promotion properties
      if (Object.keys(this.promotion).length > 0) {
        const promotionProperties =
          window.theme?.utilities?.getPromotionProperties(this.promotion);
        if (Object.keys(promotionProperties).length > 0) {
          Object.entries(promotionProperties).forEach(([key, value]) => {
            properties[`${key}`] = value.value;
          });
        }
      }

      // find other properties by looking for hidden properties inputs
      this.querySelectorAll(
        `input[type="hidden"][name^="properties["]`,
      ).forEach((el) => {
        const propertyName = el.name.match(/\[([^)]+)\]/)?.[1];
        if (propertyName) properties[propertyName] = el.value;
      });

      // set the order type
      properties["_ord_type"] = this.product?.order_type ?? "WHP";

      // set the minicart message
      if (
        this.product.hasOwnProperty("show_minicart_message") &&
        this.product.show_minicart_message === true &&
        this.variant.hasOwnProperty("minicart_message") &&
        this.variant.minicart_message.length > 0
      ) {
        properties["_minicart_message"] = this.variant.minicart_message;
      }

      // set the preorder message
      if (
        this.product.hasOwnProperty("show_preorder_message") &&
        this.product.show_preorder_message === true &&
        this.variant.hasOwnProperty("preorder_message") &&
        this.variant.preorder_message.length > 0
      ) {
        properties["_po_message"] = this.variant.preorder_message;
      }

      // set the final sale message
      if (
        this.variant.hasOwnProperty("isFinalSale") &&
        this.variant.isFinalSale === true
      ) {
        properties["_Final Sale"] = true;
      }

      // set the color id
      if (this.variant.hasOwnProperty("color")) {
        properties["_color_id"] = this.variant.color;
      }

      // set the intuitive cid
      if (window.theme?.customer?.id) {
        properties["_intuitive_cid"] = window.theme.customer.id;
      }

      properties["_add_to_cart_component"] = "product-card";

      return properties;
    }

    /**
     * Set loading state for add to cart buttons
     * @param {NodeList} buttons - The buttons to update
     * @param {boolean} isLoading - Whether to show loading state
     */
    setAddToCartLoadingState(buttons, isLoading) {
      if (!buttons) return;

      buttons.forEach((button) => {
        if (isLoading) {
          button.setAttribute("aria-disabled", true);
          button.classList.add("loading");
          button
            .querySelector(".loading-overlay__spinner")
            ?.classList.remove("hidden");
        } else {
          button.removeAttribute("aria-disabled");
          button.classList.remove("loading");
          button
            .querySelector(".loading-overlay__spinner")
            ?.classList.add("hidden");
        }
      });
    }

    /**
     * Builds the PDP navigation URL for the card's anchor links.
     *
     * When the global "Require option selection on landing" setting is on, the card
     * links to the selected color via partial hydration (?option_values=<colorId>)
     * so the PDP loads on that color with the remaining options (e.g. Size) unchosen,
     * instead of deep-linking a full variant. When the setting is off, the passed URL
     * (a full variant URL or product URL) is used unchanged.
     *
     * @param {string} preferredUrl - The URL the caller would otherwise use
     * @returns {string} The URL to apply to the card's anchor links
     */
    resolveCardNavigationUrl(preferredUrl) {
      const requireOptionSelection =
        window.theme?.settings?.pdp_require_option_selection === true;
      const colorOptionValueId = this.selectedSwatchData?.option_value_id;

      if (
        !requireOptionSelection ||
        colorOptionValueId === undefined ||
        colorOptionValueId === null ||
        colorOptionValueId === ""
      ) {
        return preferredUrl;
      }

      const baseUrl =
        this.selectedSwatchData?.url || this.product?.url || preferredUrl;
      if (!baseUrl) return preferredUrl;

      return (
        buildProductUrl(baseUrl, [String(colorOptionValueId)], { view: null }) ||
        preferredUrl
      );
    }

    /**
     * Updates all product links with a new URL
     * @param {string} url - The new URL to set
     */
    setProductLinks(url) {
      // early exit conditions
      if (!url) return;
      // resolve the navigation URL (color-only partial hydration when enabled)
      const navigationUrl = this.resolveCardNavigationUrl(url);
      // exit if no product links
      const productLinks = this.querySelectorAll("a");
      if (!productLinks) return;
      // set the product links
      productLinks.forEach((productLink) => {
        productLink.href = navigationUrl;
      });
      // get the quickview link
      const quickviewLink = this.querySelector(".product-card__quick-view-btn");
      // set the product url (quickview keeps the full URL for in-modal resolution)
      if (quickviewLink) quickviewLink.dataset.productUrl = url;
    }

    /**
     * Updates the price element with new HTML
     * @param {string} html - The HTML to set for the price
     */
    setPriceElement(html) {
      // early exit conditions
      if (!html) return;
      // exit if no price element
      if (!this.elements.priceElement) return;
      // Read the incoming markup without attaching it, so the undiscounted price never
      // paints. The element is committed once, already discounted where applicable.
      const holder = document.createElement("div");
      holder.appendChild(html);
      const nextPriceHtml = holder.innerHTML;
      const isNewPrice = this.lastPriceHtml !== nextPriceHtml;
      this.lastPriceHtml = nextPriceHtml;
      this.promotionApplied = false;

      // A new price means the variant or swatch changed, so the promotion in hand
      // belongs to the previous selection. Commit the plain price and wait for the
      // fetch that follows the change, rather than discounting this price with a
      // promotion that may not apply to it.
      if (isNewPrice) {
        this.promotion = {};
        this.elements.priceElement.replaceChildren(
          renderPriceMarkup(nextPriceHtml, null),
        );
        return;
      }

      // Same price re-rendered: the promotion still belongs to this selection, so
      // commit it already discounted in a single mutation.
      this.elements.priceElement.replaceChildren(
        renderPriceMarkup(nextPriceHtml, this.promotion),
      );
      this.promotionApplied = !!this.elements.priceElement.querySelector(
        '.price-item[data-promotion-applied="true"]',
      );
    }

    /**
     * Gets the product data
     * @returns {Object} The product data
     */
    getProduct() {
      return this.product ?? {};
    }

    /**
     * Gets the selected swatch data
     * @returns {Object|null} The selected swatch data
     */
    getSelectedSwatchData() {
      return this.selectedSwatchData ?? {};
    }

    /**
     * Backward-compatible variant getter for legacy callers.
     * Extended variants no longer ship full variant arrays in product-json.
     * @returns {Array} Array containing the current selected variant when available
     */
    getVariants() {
      return this.variant?.id ? [this.variant] : [];
    }

    /**
     * Gets the current variant
     * @returns {Object|null} The current variant
     */
    getVariant() {
      return this.variant ?? {};
    }

    /**
     * Checks if the product has color swatches
     * @returns {boolean} Whether the product has swatches
     */
    hasSwatches() {
      return this.product.swatches?.length > 0;
    }

    /**
     * Checks if this is the first grid item
     * @returns {boolean} Whether this is the first grid item
     */
    isFirstGridItem() {
      // get the grid item containing the product card
      const closestGridItem = this.closest(".product-grid__item.product-grid__item--product") || this.closest(".grid__item.grid__item--product");
      // exit if no grid item
      if (!closestGridItem) return false;
      // get the first page product grid
      const gridItemContainer = closestGridItem.closest(
        `[data-products-container][data-page="1"]`,
      );
      // exit if no first page product grid
      if (!gridItemContainer) return false;
      // get the first grid item of the first page product grid
      const firstGridItem = gridItemContainer.querySelector(
        ".product-grid__item.product-grid__item--product:first-of-type, .grid__item.grid__item--product:first-of-type",
      );
      // exit if no first grid item, which would be odd in this context at this point
      if (!firstGridItem) return false;
      const firstGridItemCard = firstGridItem.querySelector("product-card");
      if (!firstGridItemCard) return false;
      // return true if the first grid item id matches the current grid item id
      return firstGridItemCard.id === this.id;
    }

    /**
     * Lifecycle callback when the element is removed from the DOM
     * Clean up any remaining references
     */
    disconnectedCallback() {
      // Render-then-fetch: cancel any pending deferred-hydration triggers so a
      // card removed during filter/sort/pagination re-render doesn't fire a
      // fetch after detachment.
      this.teardownDeferredHydrationTriggers();
      window.removeEventListener("resize", this.boundHandleViewportModeChange);
      this.removeEventListener(
        "quick-add-to-bag:variant-change",
        this.boundHandleQuickAddVariantChange,
      );
      if (this.viewportModeRaf) {
        cancelAnimationFrame(this.viewportModeRaf);
        this.viewportModeRaf = null;
      }
      if (this.unsubscribers?.length) {
        this.unsubscribers.forEach((unsub) => unsub());
        this.unsubscribers = [];
      }
      this.prefetchedSwatchMediaUrls.clear();
      this.destroySwatchMediaSlider();
      // Clean up any remaining references
      this.product = null;
      this.variant = null;
    }
  }

  customElements.define("product-card", ProductCard);
}
